import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// API servisleri
const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_MERCHANT_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/merchant"
).replace(/\/+$/, "");

// Firebase Storage için yapılandırma (kullanılmıyor ama kalsın)
const FIREBASE_STORAGE_BUCKET = "bruh-bbadb.firebasestorage.app";
const STORAGE_BASE_PATH = "bruhmanadventures";

const PRODUCT_NAME_MAX_LENGTH = 35;
const PRODUCT_DESCRIPTION_MAX_LENGTH = 50;
const PRODUCT_VIDEO_DESCRIPTION_MAX_LENGTH = 500;

const PRODUCT_STANDARD_DURATION_OPTIONS = [
  { value: "", label: "Seçilmedi" },
  { value: "0", label: "+0" },
  { value: "15m", label: "+15 dk" },
  { value: "30m", label: "+30 dk" },
  { value: "45m", label: "+45 dk" },
  { value: "1h", label: "+1 saat" },
  { value: "2h", label: "+2 saat" },
  { value: "3h", label: "+3 saat" },
  { value: "4h", label: "+4 saat" },
  { value: "all_day", label: "Tüm gün" },
];

const PRODUCT_DAY_DURATION_OPTIONS = [
  { value: "", label: "Seçilmedi" },
  { value: "0", label: "+0" },
  { value: "1d", label: "1 gün" },
  { value: "2d", label: "2 gün" },
  { value: "3d", label: "3 gün" },
  { value: "4d", label: "4 gün" },
  { value: "5d", label: "5 gün" },
  { value: "6d", label: "6 gün" },
  { value: "7d", label: "7 gün" },
  { value: "15d", label: "15 gün" },
  { value: "1mo", label: "1 ay" },
];

const PRODUCT_STANDARD_DURATION_VALUES = new Set(PRODUCT_STANDARD_DURATION_OPTIONS.map((option) => option.value));
const PRODUCT_DAY_DURATION_VALUES = new Set(PRODUCT_DAY_DURATION_OPTIONS.map((option) => option.value));
const PRODUCT_DURATION_VALUES = new Set([
  ...PRODUCT_STANDARD_DURATION_VALUES,
  ...PRODUCT_DAY_DURATION_VALUES,
]);

const normalizeProductDurationValue = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return PRODUCT_DURATION_VALUES.has(normalized) ? normalized : "";
};

const normalizeStandardDurationValue = (value) => {
  const normalized = normalizeProductDurationValue(value);
  return PRODUCT_STANDARD_DURATION_VALUES.has(normalized) ? normalized : "";
};

const normalizeDayDurationValue = (value) => {
  const normalized = normalizeProductDurationValue(value);
  return PRODUCT_DAY_DURATION_VALUES.has(normalized) ? normalized : "";
};

const normalizeDurationForDayMode = (value, dayEnabled) =>
  dayEnabled ? normalizeDayDurationValue(value) : normalizeStandardDurationValue(value);

const getProductDurationOptions = (dayEnabled) =>
  dayEnabled ? PRODUCT_DAY_DURATION_OPTIONS : PRODUCT_STANDARD_DURATION_OPTIONS;

const PRODUCT_LANGUAGE_CODES = [
  "tr", "en", "de", "fr", "es", "it", "pt", "ru", "ar", "fa", "ur", "az", "kk", "uz", "tk",
  "ky", "tg", "zh", "ja", "ko", "hi", "bn", "pa", "gu", "mr", "ne", "si", "ta", "te", "kn",
  "ml", "th", "vi", "id", "ms", "fil", "my", "km", "lo", "he", "el", "bg", "ro", "hu", "pl",
  "cs", "sk", "sl", "hr", "sr", "bs", "mk", "sq", "uk", "be", "lt", "lv", "et", "fi", "sv",
  "no", "da", "is", "nl", "af", "sw", "am", "so", "ha", "yo", "ig", "zu", "xh", "rw", "mg",
  "ka", "hy", "eu", "ca", "gl", "cy", "ga", "mt", "lb", "eo", "la", "mn", "ps", "ku", "sd",
  "jv", "su", "mi", "sm", "to", "ht"
];

const PRODUCT_LANGUAGE_FALLBACKS = {
  tr: "Türkçe",
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  ru: "Русский",
  ar: "العربية",
  fa: "فارسی",
  az: "Azərbaycan dili",
  zh: "中文",
  ja: "日本語",
  ko: "한국어",
  hi: "हिन्दी",
};

const getProductLanguageLabel = (code) => {
  const safeCode = String(code || "tr").trim().toLowerCase();
  if (PRODUCT_LANGUAGE_FALLBACKS[safeCode]) return PRODUCT_LANGUAGE_FALLBACKS[safeCode];
  try {
    const value = new Intl.DisplayNames([safeCode], { type: "language" }).of(safeCode);
    if (value && value !== safeCode) {
      return value.charAt(0).toLocaleUpperCase(safeCode) + value.slice(1);
    }
  } catch {
    // Intl tarafından desteklenmeyen dil kodlarında aşağıdaki fallback kullanılır.
  }
  return safeCode.toUpperCase();
};

const PRODUCT_LANGUAGE_OPTIONS = PRODUCT_LANGUAGE_CODES.map((code) => ({
  code,
  label: getProductLanguageLabel(code),
})).sort((a, b) => a.label.localeCompare(b.label, "tr"));

const normalizeProductLanguageCode = (value, fallback = "tr") => {
  const code = String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 20);
  return code || fallback;
};

const normalizeProductVideoId = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 30);
  return digits.length === 30 ? digits : "";
};

const createEmptyCategoryLanguageData = (languageCode = "tr") => {
  const code = normalizeProductLanguageCode(languageCode);
  return {
    languageCode: code,
    language: getProductLanguageLabel(code),
    label: "",
  };
};

const normalizeCategoryLocalizedData = (category = {}) => {
  const input = Array.isArray(category?.localizedData)
    ? category.localizedData
    : Array.isArray(category?.translations)
      ? category.translations
      : [];
  const seen = new Set();
  const localizedData = [];

  input.forEach((raw) => {
    const languageCode = normalizeProductLanguageCode(raw?.languageCode || raw?.code || "tr");
    if (seen.has(languageCode)) return;
    seen.add(languageCode);
    localizedData.push({
      languageCode,
      language: getProductLanguageLabel(languageCode),
      label: String(raw?.label ?? raw?.name ?? "").trim().slice(0, 100),
    });
  });

  if (!seen.has("tr")) {
    localizedData.unshift({
      ...createEmptyCategoryLanguageData("tr"),
      label: String(category?.label || "").trim().slice(0, 100),
    });
  }

  return localizedData.length > 0 ? localizedData : [createEmptyCategoryLanguageData("tr")];
};

const syncCategoryCompatibilityFields = (category = {}) => {
  const localizedData = normalizeCategoryLocalizedData(category);
  const turkish = localizedData.find((item) => item.languageCode === "tr") || localizedData[0];
  return {
    ...category,
    label: String(turkish?.label || category?.label || "").trim(),
    languages: localizedData.map((item) => item.languageCode),
    localizedData,
  };
};

const getCategoryLanguageData = (category, languageCode = "tr") => {
  const code = normalizeProductLanguageCode(languageCode);
  return normalizeCategoryLocalizedData(category).find((item) => item.languageCode === code) || createEmptyCategoryLanguageData(code);
};

const createEmptyProductLanguageData = (languageCode = "tr") => {
  const code = normalizeProductLanguageCode(languageCode);
  return {
    languageCode: code,
    language: getProductLanguageLabel(code),
    name: "",
    description: "",
    basePrice: 0,
    kdv: 10,
    discountEnabled: false,
    discountRate: 0,
    departmanno: "",
    active: true,
    videoUrl: "",
    videoId: "",
    videoDescription: "",
    videoVersion: 1,
    videoUpdatedAt: null,
    personnelEnabled: false,
    timeEnabled: false,
    dayEnabled: false,
    questions: [],
  };
};

const normalizeQuestionsForProductLanguage = (questions, dayEnabled = null) =>
  Array.isArray(questions)
    ? questions.map((q) => ({
        ...q,
        id: q?.id ?? Date.now(),
        text: String(q?.text || ""),
        type: q?.type === "single" ? "single" : "multiple",
        isRequired: !!q?.isRequired,
        hasQuantity: !!q?.hasQuantity,
        image: q?.image ? String(q.image) : "",
        options: Array.isArray(q?.options)
          ? q.options.map((o) => ({
              ...o,
              id: o?.id ?? Date.now(),
              name: String(o?.name || ""),
              image: o?.image ? String(o.image) : "",
              hasQuantity: !!o?.hasQuantity,
              durationEnabled: !!o?.durationEnabled,
              durationVisible: o?.durationVisible !== false,
              durationValue: !!o?.durationEnabled
                ? dayEnabled === null
                  ? normalizeProductDurationValue(o?.durationValue ?? o?.duration)
                  : normalizeDurationForDayMode(o?.durationValue ?? o?.duration, dayEnabled)
                : "",
              price: round2(Number(parseNumLoose(o?.price) ?? 0)),
              kdvoption: Number.isFinite(Number(o?.kdvoption)) ? Number(o.kdvoption) : 10,
              discountEnabled: !!o?.discountEnabled,
              discountRate: normalizeDiscountRate(o?.discountRate),
              departmanno: normalizeDepartmentNo(o?.departmanno ?? o?.departmentNo ?? o?.departmentno ?? ""),
            }))
          : [],
      }))
    : [];

const normalizeProductLocalizedData = (product) => {
  const source = Array.isArray(product?.localizedData)
    ? product.localizedData
    : Array.isArray(product?.translations)
      ? product.translations
      : [];
  const seen = new Set();
  const localizedData = [];

  source.forEach((raw) => {
    const languageCode = normalizeProductLanguageCode(raw?.languageCode || raw?.code || "tr");
    if (seen.has(languageCode)) return;
    seen.add(languageCode);
    const dayEnabled = !!raw?.dayEnabled;
    localizedData.push({
      ...createEmptyProductLanguageData(languageCode),
      languageCode,
      language: String(raw?.language || getProductLanguageLabel(languageCode)),
      name: getLimitedText(raw?.name || "", PRODUCT_NAME_MAX_LENGTH),
      description: getLimitedText(raw?.description ?? raw?.desc ?? "", PRODUCT_DESCRIPTION_MAX_LENGTH),
      basePrice: round2(Number(parseNumLoose(raw?.basePrice ?? raw?.price) ?? (languageCode === "tr" ? parseNumLoose(product?.basePrice ?? product?.price) ?? 0 : 0))),
      kdv: Number.isFinite(Number(raw?.kdv)) ? Number(raw.kdv) : languageCode === "tr" && Number.isFinite(Number(product?.kdv)) ? Number(product.kdv) : 10,
      discountEnabled: raw?.discountEnabled !== undefined ? !!raw.discountEnabled : languageCode === "tr" ? !!product?.discountEnabled : false,
      discountRate: normalizeDiscountRate(raw?.discountRate ?? (languageCode === "tr" ? product?.discountRate : 0)),
      departmanno: normalizeDepartmentNo(raw?.departmanno ?? raw?.departmentNo ?? raw?.departmentno ?? (languageCode === "tr" ? product?.departmanno : "")),
      active: raw?.active !== false,
      videoUrl: String(raw?.videoUrl || ""),
      videoId: normalizeProductVideoId(raw?.videoId),
      videoDescription: getLimitedText(raw?.videoDescription || "", PRODUCT_VIDEO_DESCRIPTION_MAX_LENGTH),
      videoVersion: Math.max(1, Number(raw?.videoVersion || 1)),
      videoUpdatedAt: raw?.videoUpdatedAt || null,
      personnelEnabled: dayEnabled ? false : !!raw?.personnelEnabled,
      timeEnabled: dayEnabled ? false : !!raw?.timeEnabled,
      dayEnabled,
      questions: normalizeQuestionsForProductLanguage(raw?.questions, dayEnabled),
    });
  });

  if (!seen.has("tr")) {
    const dayEnabled = !!product?.dayEnabled;
    localizedData.unshift({
      ...createEmptyProductLanguageData("tr"),
      name: getLimitedText(product?.name || "", PRODUCT_NAME_MAX_LENGTH),
      description: getLimitedText(product?.description ?? product?.desc ?? "", PRODUCT_DESCRIPTION_MAX_LENGTH),
      basePrice: round2(Number(parseNumLoose(product?.basePrice ?? product?.price) ?? 0)),
      kdv: Number.isFinite(Number(product?.kdv)) ? Number(product.kdv) : 10,
      discountEnabled: !!product?.discountEnabled,
      discountRate: normalizeDiscountRate(product?.discountRate),
      departmanno: normalizeDepartmentNo(product?.departmanno ?? product?.departmentNo ?? product?.departmentno ?? ""),
      active: product?.active !== false,
      videoUrl: String(product?.videoUrl || ""),
      videoId: normalizeProductVideoId(product?.videoId),
      videoDescription: getLimitedText(product?.videoDescription || "", PRODUCT_VIDEO_DESCRIPTION_MAX_LENGTH),
      videoVersion: Math.max(1, Number(product?.videoVersion || 1)),
      videoUpdatedAt: product?.videoUpdatedAt || null,
      personnelEnabled: dayEnabled ? false : !!product?.personnelEnabled,
      timeEnabled: dayEnabled ? false : !!product?.timeEnabled,
      dayEnabled,
      questions: normalizeQuestionsForProductLanguage(product?.questions, dayEnabled),
    });
  }

  return localizedData;
};

const getProductLanguageData = (product, languageCode = "tr") => {
  const code = normalizeProductLanguageCode(languageCode);
  const localizedData = Array.isArray(product?.localizedData) ? product.localizedData : [];
  return localizedData.find((item) => normalizeProductLanguageCode(item?.languageCode) === code) || null;
};

const syncProductCompatibilityFields = (product) => {
  const localizedData = Array.isArray(product?.localizedData) ? product.localizedData : [];
  const turkish = localizedData.find((item) => normalizeProductLanguageCode(item?.languageCode) === "tr") || localizedData[0] || createEmptyProductLanguageData("tr");
  return {
    ...product,
    localizedData,
    languages: localizedData.map((item) => normalizeProductLanguageCode(item?.languageCode)),
    // Eski kiosk/rapor ekranlarının çalışmaya devam etmesi için Türkçe içerik üst seviyede aynalanır.
    name: turkish.name || "",
    description: turkish.description || "",
    basePrice: round2(Number(parseNumLoose(turkish.basePrice) ?? 0)),
    kdv: Number.isFinite(Number(turkish.kdv)) ? Number(turkish.kdv) : 10,
    discountEnabled: !!turkish.discountEnabled,
    discountRate: normalizeDiscountRate(turkish.discountRate),
    departmanno: normalizeDepartmentNo(turkish.departmanno),
    active: turkish.active !== false,
    questions: normalizeQuestionsForProductLanguage(turkish.questions, !!turkish.dayEnabled),
    videoUrl: turkish.videoUrl || "",
    videoId: normalizeProductVideoId(turkish.videoId),
    videoDescription: turkish.videoDescription || "",
    videoVersion: Math.max(1, Number(turkish.videoVersion || 1)),
    videoUpdatedAt: turkish.videoUpdatedAt || null,
    personnelEnabled: !!turkish.personnelEnabled,
    timeEnabled: !!turkish.timeEnabled,
    dayEnabled: !!turkish.dayEnabled,
    sharedTimeValue: normalizeStandardDurationValue(product?.sharedTimeValue ?? product?.sharedTime),
    showOffersInsteadOfPrice: !!product?.showOffersInsteadOfPrice,
    menuProductIsMain: !!product?.menuProductIsMain,
    addMainProductToReceipt: !!product?.addMainProductToReceipt && !product?.menuProductIsMain,
    showRecommendedProducts: !!product?.showRecommendedProducts,
  };
};

const updateProductLanguageData = (product, languageCode, updater) => {
  const code = normalizeProductLanguageCode(languageCode);
  const localizedData = normalizeProductLocalizedData(product);
  const index = localizedData.findIndex((item) => normalizeProductLanguageCode(item?.languageCode) === code);
  const current = index >= 0 ? localizedData[index] : createEmptyProductLanguageData(code);
  const next = typeof updater === "function" ? updater({ ...current }) : { ...current, ...(updater || {}) };
  const dayEnabled = !!next?.dayEnabled;
  const normalizedNext = {
    ...createEmptyProductLanguageData(code),
    ...next,
    languageCode: code,
    language: getProductLanguageLabel(code),
    name: getLimitedText(next?.name || "", PRODUCT_NAME_MAX_LENGTH),
    description: getLimitedText(next?.description || "", PRODUCT_DESCRIPTION_MAX_LENGTH),
    basePrice: round2(Number(parseNumLoose(next?.basePrice) ?? 0)),
    kdv: Number.isFinite(Number(next?.kdv)) ? Number(next.kdv) : 10,
    discountEnabled: !!next?.discountEnabled,
    discountRate: normalizeDiscountRate(next?.discountRate),
    departmanno: normalizeDepartmentNo(next?.departmanno ?? next?.departmentNo ?? next?.departmentno ?? ""),
    active: next?.active !== false,
    videoDescription: getLimitedText(next?.videoDescription || "", PRODUCT_VIDEO_DESCRIPTION_MAX_LENGTH),
    questions: normalizeQuestionsForProductLanguage(next?.questions, dayEnabled),
    personnelEnabled: dayEnabled ? false : !!next?.personnelEnabled,
    timeEnabled: dayEnabled ? false : !!next?.timeEnabled,
    dayEnabled,
  };
  const nextLocalizedData = [...localizedData];
  if (index >= 0) nextLocalizedData[index] = normalizedNext;
  else nextLocalizedData.push(normalizedNext);
  return syncProductCompatibilityFields({ ...product, localizedData: nextLocalizedData });
};

const getLimitedText = (value, maxLength) => String(value ?? "").slice(0, maxLength);

const getPreviewTitleTextClass = (text) => {
  const len = String(text || "").trim().length;
  if (len > 30) return "text-[10px] leading-tight line-clamp-2 min-h-[26px]";
  if (len > 24) return "text-[11px] leading-tight line-clamp-2 min-h-[26px]";
  if (len > 18) return "text-xs leading-tight line-clamp-2 min-h-[30px]";
  return "text-sm leading-tight line-clamp-1 min-h-[18px]";
};

const getPreviewDescriptionTextClass = (text) => {
  const len = String(text || "").trim().length;
  if (len > 45) return "text-[10px] leading-tight line-clamp-3 min-h-[34px]";
  if (len > 35) return "text-[11px] leading-snug line-clamp-3 min-h-[38px]";
  return "text-xs leading-relaxed line-clamp-2 min-h-[32px]";
};

const getPreviewPriceTextClass = (text) => {
  const len = String(text || "").trim().length;
  if (len > 10) return "text-xs px-3 py-2";
  if (len > 8) return "text-sm px-3 py-2";
  return "text-base px-4 py-2";
};

// ===============================
// ✅ DEPARTMENT HELPERS
// ===============================
const normalizeDepartmentNo = (value) =>
  String(value ?? "")
    .trim()
    .replace(/,/g, "")
    .replace(/\s+/g, "")
    .slice(0, 20);

const parseDepartmentEntry = (entry) => {
  const raw = String(entry ?? "").trim();
  if (!raw) return null;

  const [namePart = "", numberPart = "", vatPart = ""] = raw.split(",");
  const name = String(namePart || "").trim();
  const number = normalizeDepartmentNo(numberPart);
  const vatRate = String(vatPart || "").trim();

  if (!name || !number) return null;

  return {
    name,
    number,
    vatRate,
    label: `${name} - Departman ${number}${vatRate ? ` - KDV %${vatRate}` : ""}`,
    raw,
  };
};

const parseDepartments = (value) => {
  const input = Array.isArray(value)
    ? value
    : typeof value === "string" && value.trim()
      ? [value]
      : [];

  const seen = new Set();
  const out = [];

  for (const item of input) {
    const parsed = parseDepartmentEntry(item);
    if (!parsed || seen.has(parsed.number)) continue;
    seen.add(parsed.number);
    out.push(parsed);
  }

  return out;
};

const getProductIdentity = (product) =>
  String(product?.uniqcode ?? product?.id ?? product?._id ?? "").trim();

// ===============================
// ✅ TL + KDV HELPERS
// ===============================
const VAT_RATES = [10, 20, 1, 0];
const vatFactor = (rate) => 1 + Number(rate || 0) / 100;

const round4 = (n) => {
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 10000) / 10000;
};

const toFixed4 = (n) => {
  const v = round4(Number(n));
  if (!Number.isFinite(v)) return "0.0000";
  return v.toFixed(4);
};

const round2 = (n) => {
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

const toFixed2 = (n) => {
  const v = round2(Number(n));
  if (!Number.isFinite(v)) return "0.00";
  return v.toFixed(2);
};

const normalizeDiscountRate = (value) => {
  const n = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, round2(n)));
};

const getDiscountBreakdown = (grossValue, enabled, rateValue) => {
  const originalGross = round2(Math.max(0, Number(grossValue || 0)));
  const discountRate = enabled ? normalizeDiscountRate(rateValue) : 0;
  const discountedGross = enabled
    ? round2(originalGross * (1 - discountRate / 100))
    : originalGross;
  return {
    originalGross,
    discountRate,
    discountAmount: round2(originalGross - discountedGross),
    discountedGross,
  };
};

const parseNumLoose = (str) => {
  if (str === "" || str === null || str === undefined) return null;
  let s = String(str).replace(/[₺\s]/g, "").trim();
  if (s === "") return null;

  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(",", ".");
  }

  s = s.replace(/[^0-9.-]/g, "");
  if (s === "" || s === "-" || s === "." || s === "-.") return null;

  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n;
};

const formatTRY = (value, maxFrac = 2) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: maxFrac,
    minimumFractionDigits: Math.min(maxFrac, 2),
  }).format(Number.isFinite(n) ? n : 0);
};

const normalizeCategoryOrderValue = (value, fallback = 1) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return Math.max(1, Number(fallback) || 1);
  return Math.trunc(n);
};

const normalizeAndSortCategories = (categories = []) => {
  return [...categories]
    .map((category, index) => ({
      ...syncCategoryCompatibilityFields(category),
      sira: normalizeCategoryOrderValue(category?.sira, index + 1),
    }))
    .sort((a, b) => {
      const bySira = normalizeCategoryOrderValue(a?.sira, 999999) - normalizeCategoryOrderValue(b?.sira, 999999);
      if (bySira !== 0) return bySira;
      return String(a?.label || "").localeCompare(String(b?.label || ""), "tr");
    });
};

const getNextCategorySira = (categories = []) => {
  if (!Array.isArray(categories) || categories.length === 0) return 1;
  const maxSira = categories.reduce((max, category) => {
    const n = normalizeCategoryOrderValue(category?.sira, 0);
    return n > max ? n : max;
  }, 0);
  return maxSira + 1;
};

const buildSwappedCategoriesByDisplayOrder = (categories, sourceLiability, requestedOrder) => {
  const safeRequested = normalizeCategoryOrderValue(requestedOrder, 1);
  const sortedCategories = normalizeAndSortCategories(categories);

  if (!sortedCategories.length) return sortedCategories;

  const clampedOrder = Math.min(Math.max(safeRequested, 1), sortedCategories.length);
  const sourceCategory = sortedCategories.find((category) => category.liability === sourceLiability);
  const targetCategory = sortedCategories[clampedOrder - 1];

  if (!sourceCategory || !targetCategory) {
    return sortedCategories;
  }

  if (sourceCategory.liability === targetCategory.liability) {
    return sortedCategories;
  }

  const sourceSira = normalizeCategoryOrderValue(sourceCategory.sira, 1);
  const targetSira = normalizeCategoryOrderValue(targetCategory.sira, clampedOrder);

  const swapped = categories.map((category) => {
    if (category.liability === sourceCategory.liability) {
      return { ...category, sira: targetSira };
    }
    if (category.liability === targetCategory.liability) {
      return { ...category, sira: sourceSira };
    }
    return category;
  });

  return normalizeAndSortCategories(swapped);
};

const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");

const redirectToLogin = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  window.location.href = "/mlogin";
};

const makeApiRequest = async (url, options = {}) => {
  const token = getAuthToken();
  const card = getAuthCard();

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let requestBody = options.body;

  if (requestBody && card) {
    try {
      const bodyData = JSON.parse(requestBody);
      if (!bodyData.card) {
        bodyData.card = card;
        requestBody = JSON.stringify(bodyData);
      }
    } catch (error) {
      // ignore
    }
  }

  const config = {
    ...options,
    headers,
    body: requestBody,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (errorData.redirectToLogin) {
        redirectToLogin();
        throw new Error("Kimlik doğrulama başarısız. Giriş sayfasına yönlendiriliyor.");
      }

      throw new Error(errorData.message || `HTTP hatası! durum: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("API İstek Hatası:", error);
    throw error;
  }
};

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

const apiService = {
  async getCategories() {
    return await makeApiRequest(`${API_BASE_URL}/getcategories`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  async getProductsByCategory(card) {
    return await makeApiRequest(`${API_BASE_URL}/getproductsbycategory`, {
      method: "POST",
      body: JSON.stringify({ card }),
    });
  },

  async getAllProducts(categories) {
    const productPromises = categories.map((category) => this.getProductsByCategory(category.card));
    const productsResponses = await Promise.all(productPromises);

    const allProducts = {};
    categories.forEach((category, index) => {
      if (productsResponses[index]?.success) {
        allProducts[category.liability] = productsResponses[index].products;
      } else {
        allProducts[category.liability] = [];
      }
    });

    return allProducts;
  },

  async getFisBilgi() {
    return await makeApiRequest(`${API_BASE_URL}/getfisbilgi`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  async uploadImageToR2(file, fileName, folder = "uploads") {
    const token = getAuthToken();
    const card = getAuthCard();
    if (!token || !card) throw new Error("Kimlik doğrulama başarısız. Lütfen tekrar giriş yapın.");

    const base64 = await blobToBase64(file);
    const contentType = file.type || "image/jpeg";

    const response = await makeApiRequest(`${API_BASE_URL}/uploadimagetor2`, {
      method: "POST",
      body: JSON.stringify({
        card,
        base64,
        contentType,
        folder,
        filename: fileName,
      }),
    });

    if (response.success && response.result && response.result.url) {
      return response.result.url;
    }

    throw new Error(response.message || "R2 yükleme başarısız: " + (response.error || "Bilinmeyen hata"));
  },


  async uploadProductVideo(file, uniqcode, languageCode) {
    const token = getAuthToken();
    const card = getAuthCard();
    if (!token || !card) throw new Error("Kimlik doğrulama başarısız. Lütfen tekrar giriş yapın.");

    const base64 = await blobToBase64(file);
    const response = await makeApiRequest(`${API_BASE_URL}/uploadproductmedia`, {
      method: "POST",
      body: JSON.stringify({
        card,
        uniqcode,
        languageCode: normalizeProductLanguageCode(languageCode),
        mediaKind: "video",
        base64,
        contentType: file.type || "video/mp4",
        filename: file.name || `product-video-${Date.now()}.mp4`,
      }),
    });

    const url = response?.result?.url || response?.url;
    const videoId = normalizeProductVideoId(response?.result?.videoId || response?.videoId);
    if (response.success && url && videoId) return { url, videoId };
    throw new Error(response.message || "Video yüklenemedi veya 30 haneli video numarası oluşturulamadı");
  },

  async addCategory(categoryData) {
    return await makeApiRequest(`${API_BASE_URL}/addcategory`, {
      method: "POST",
      body: JSON.stringify(categoryData),
    });
  },

  async updateCategory(categoryData) {
    return await makeApiRequest(`${API_BASE_URL}/updatecategory`, {
      method: "POST",
      body: JSON.stringify(categoryData),
    });
  },

  async deleteCategory(liability) {
    return await makeApiRequest(`${API_BASE_URL}/deletecategory`, {
      method: "POST",
      body: JSON.stringify({ liability }),
    });
  },

  async saveProducts(products) {
    return await makeApiRequest(`${API_BASE_URL}/saveproducts`, {
      method: "POST",
      body: JSON.stringify({ products }),
    });
  },

  async saveProduct(product) {
    return await makeApiRequest(`${API_BASE_URL}/saveproduct`, {
      method: "POST",
      body: JSON.stringify({ product }),
    });
  },

  async addProduct(productData) {
    return await makeApiRequest(`${API_BASE_URL}/addproduct`, {
      method: "POST",
      body: JSON.stringify(productData),
    });
  },

  async removeProduct(uniqcode) {
    return await makeApiRequest(`${API_BASE_URL}/removeproduct`, {
      method: "POST",
      body: JSON.stringify({ uniqcode }),
    });
  },

  async swapCategoryOrder(payload) {
    return await makeApiRequest(`${API_BASE_URL}/swapcategoryorder`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

const preloadImage = (src) => {
  return new Promise((resolve) => {
    if (!src) return resolve(false);
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
};

const preloadAllImages = async (allProductsData, categories) => {
  const imageUrls = new Set();

  categories.forEach((category) => {
    if (category.icon) imageUrls.add(category.icon);
  });

  Object.values(allProductsData).forEach((products) => {
    products.forEach((product) => {
      if (product.image) imageUrls.add(product.image);
      if (product.questions) {
        product.questions.forEach((question) => {
          if (question.image) imageUrls.add(question.image);
          if (question.options) {
            question.options.forEach((option) => {
              if (option.image) imageUrls.add(option.image);
            });
          }
        });
      }
    });
  });

  const preloadPromises = Array.from(imageUrls).map((url) => preloadImage(url));
  await Promise.allSettled(preloadPromises);
};

const sanitizeMoneyInput = (raw) => {
  if (raw === null || raw === undefined) return "";

  let s = String(raw).replace(/[₺\s]/g, "").replace(/,/g, ".").trim();
  s = s.replace(/[^0-9.]/g, "");

  if (s === "") return "";

  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    const before = s.slice(0, firstDot);
    const after = s.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
    const intPart = before === "" ? "0" : before;
    return `${intPart}.${after}`;
  }

  return s;
};

const netDisplay4Minus1 = (netRaw) => {
  const x = Number(netRaw);
  if (!Number.isFinite(x) || x <= 0) return "0.0000";

  const units = Math.round((x + Number.EPSILON) * 10000);
  const adj = Math.max(0, units - 1);
  return (adj / 10000).toFixed(4);
};

const VatPriceInput = ({
  grossValue,
  vatRate,
  onChangeGross,
  onChangeVat,
  errorKey,
  setError,
  size = "md",
  showEntryNote = false,
}) => {
  const [activeField, setActiveField] = useState(null);
  const [grossStr, setGrossStr] = useState("0.00");

  const grossRef = useRef(null);
  const pendingCaretRef = useRef(null);

  const safeVat = Number.isFinite(Number(vatRate)) ? Number(vatRate) : 10;
  const factor = vatFactor(safeVat);

  const requestCaret = (pos) => {
    pendingCaretRef.current = { pos };
  };

  useLayoutEffect(() => {
    const c = pendingCaretRef.current;
    if (!c) return;

    const el = grossRef.current;
    if (!el) {
      pendingCaretRef.current = null;
      return;
    }

    if (document?.activeElement !== el) {
      pendingCaretRef.current = null;
      return;
    }

    const p = Math.max(0, Math.min(Number(c.pos || 0), String(el.value || "").length));
    try {
      el.setSelectionRange(p, p);
    } catch {
      // ignore
    }
    pendingCaretRef.current = null;
  }, [grossStr]);

  useEffect(() => {
    const gross = Number(grossValue || 0);
    const nextGrossStr = toFixed2(gross);
    if (activeField !== "gross") setGrossStr(nextGrossStr);
  }, [grossValue, activeField]);

  const validate = (n) => {
    if (n === null) return "Lütfen bir fiyat girin";
    if (!Number.isFinite(n) || n < 0) return "Lütfen geçerli bir fiyat girin";
    return "";
  };

  const commitGross = (num) => {
    const g2 = round2(Number(num || 0));
    onChangeGross(g2);
  };

  const handleDecimalOverwriteKeyDown = (e) => {
    const key = e.key;
    if (!/^\d$/.test(key)) return;

    const el = grossRef.current;
    if (!el) return;

    const selStart = el.selectionStart;
    const selEnd = el.selectionEnd;
    if (selStart == null || selEnd == null) return;
    if (selStart !== selEnd) return;

    const cur = grossStr;
    const dot = cur.indexOf(".");
    if (dot === -1) return;

    const dec = cur.slice(dot + 1);
    if (dec.length !== 2) return;

    const decStart = dot + 1;
    const decEnd = dot + 2;
    if (selStart < decStart || selStart > decEnd) return;

    e.preventDefault();

    const rel = selStart - decStart;
    const nextDec = dec.substring(0, rel) + key + dec.substring(rel + 1);
    const nextStr = cur.slice(0, dot + 1) + nextDec;

    setActiveField("gross");
    setGrossStr(nextStr);

    requestCaret(selStart + 1);

    const num = parseNumLoose(nextStr);
    const msg = validate(num);
    setError((prev) => ({ ...prev, [errorKey]: msg }));
    if (msg) return;

    commitGross(num);
  };

  const handleGrossChange = (e) => {
    const caretPosRaw = e.target.selectionStart ?? String(e.target.value || "").length;
    const nextStr = sanitizeMoneyInput(e.target.value);

    setActiveField("gross");
    setGrossStr(nextStr);

    requestCaret(Math.min(caretPosRaw, nextStr.length));

    const num = parseNumLoose(nextStr);
    const msg = validate(num);
    setError((prev) => ({ ...prev, [errorKey]: msg }));

    if (msg) return;
    commitGross(num);
  };

  const handleGrossBlur = () => {
    setActiveField(null);

    const num = parseNumLoose(grossStr);

    if (num !== null && Number.isFinite(num) && num >= 0) {
      setGrossStr(toFixed2(num));
      commitGross(num);
    } else {
      setGrossStr("0.00");
      commitGross(0);
    }
  };

  const handleVatChange = (newRate) => {
    const rate = Number(newRate);
    onChangeVat(rate);
  };

  const netDisplayStr = useMemo(() => {
    const gFromStr = parseNumLoose(grossStr);
    const g = Number.isFinite(Number(gFromStr)) ? Number(gFromStr) : Number(grossValue || 0);
    const netRaw = factor ? g / factor : g;
    return netDisplay4Minus1(netRaw);
  }, [grossStr, grossValue, factor]);

  const boxW = size === "sm" ? "w-28" : "w-40";
  const font = size === "sm" ? "text-sm" : "text-lg md:text-xl";
  const symbolFont = size === "sm" ? "text-xs" : "text-gray-500";

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        <div className={`flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 ${boxW}`}>
          <span className={symbolFont}>₺</span>
          <div className={`${font} font-semibold text-gray-800 outline-none w-full bg-transparent select-text`}>
            {netDisplayStr}
          </div>
        </div>

        <span className="text-gray-400 font-bold">×</span>

        <div className="flex items-center gap-2">
          <select
            value={Number(safeVat ?? 10)}
            onChange={(e) => handleVatChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
          >
            {VAT_RATES.map((r) => (
              <option key={r} value={r}>
                %{r}
              </option>
            ))}
          </select>
          <span className="text-gray-400 font-bold">=</span>
        </div>

        <div className={`flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 ${boxW}`}>
          <span className={symbolFont}>₺</span>
          <input
            ref={grossRef}
            type="text"
            value={grossStr}
            onFocus={() => setActiveField("gross")}
            onBlur={handleGrossBlur}
            onChange={handleGrossChange}
            onKeyDown={handleDecimalOverwriteKeyDown}
            className={`${font} font-semibold text-green-600 outline-none w-full bg-transparent`}
            placeholder="0.00"
            inputMode="decimal"
          />
        </div>
      </div>

      {showEntryNote && (
        <p className="text-[11px] text-gray-500">
          Sadece buradan fiyat bilgisi girilebilir, KDVsiz fiyat alanından fiyat girilemez.
        </p>
      )}
    </div>
  );
};

const ProductImage = ({ src, alt, onImageChange, uploading = false }) => {
  const [imageError, setImageError] = useState(!src);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (src) {
      setImageError(false);
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="relative w-full max-w-md aspect-video bg-gray-200 rounded-xl overflow-hidden flex items-center justify-center">
      {!imageError && src ? (
        <>
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-300 animate-pulse flex items-center justify-center">
              <div className="text-gray-500 text-xs">Yükleniyor...</div>
            </div>
          )}
          <img
            src={src}
            alt={alt}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />

          <label className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
            <span className="text-white text-2xl mb-1">📷</span>
            <span className="text-white text-sm font-medium">Fotoğrafı Değiştir</span>
            <span className="mt-1 text-[11px] text-white/80">Ortak ürün fotoğrafı · 16:9</span>
            <input type="file" className="hidden" accept="image/*" onChange={onImageChange} />
          </label>
        </>
      ) : (
        <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer bg-slate-200">
          <span className="text-3xl mb-1">📷</span>
          <span className="text-slate-700 text-sm font-semibold">Ortak Fotoğraf Ekle</span>
          <span className="mt-1 text-[11px] text-slate-500">16:9 önerilir · bütün dillerde ortak</span>
          <input type="file" className="hidden" accept="image/*" onChange={onImageChange} />
        </label>
      )}

      {uploading && (
        <div className="absolute inset-0 bg-black bg-opacity-55 flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
            <p className="text-sm">Yükleniyor...</p>
          </div>
        </div>
      )}
    </div>
  );
};

const OptionImage = ({ src, alt, onImageChange, uploading = false }) => {
  const [imageError, setImageError] = useState(!src);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (src) {
      setImageError(false);
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="relative w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
      {!imageError && src ? (
        <>
          <div className="w-32 h-32 relative">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg flex items-center justify-center">
                <div className="text-gray-500 text-xs">Yükleniyor...</div>
              </div>
            )}
            <img
              src={src}
              alt={alt}
              className={`w-32 h-32 object-cover rounded-lg transition-opacity duration-300 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </div>

          <label className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex flex-col items-center justify-center cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
            <span className="text-white text-lg mb-1">📷</span>
            <span className="text-white text-xs font-medium">Değiştir</span>
            <input type="file" className="hidden" accept="image/*" onChange={onImageChange} />
          </label>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
          <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center p-1">
            <span className="text-white text-lg mb-1">📷</span>
            <span className="text-white text-xs font-medium">Fotoğraf Ekle</span>
            <input type="file" className="hidden" accept="image/*" onChange={onImageChange} />
          </div>
        </label>
      )}

      {uploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-1"></div>
            <p className="text-xs">Yükleniyor...</p>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductPreviewCard = ({ product }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(!product?.image);

  useEffect(() => {
    if (product?.image) {
      setImageError(false);
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = product.image;
    }
  }, [product?.image]);

  if (!product) return null;

  const productName = String(product.name || "İsim girilmedi");
  const productDescription = String(product.description || "Açıklama bulunmuyor");
  const discount = getDiscountBreakdown(product.basePrice, product.discountEnabled, product.discountRate);
  const priceText = product.showOffersInsteadOfPrice
    ? "Teklifleri Gör"
    : product.discountEnabled && discount.discountRate > 0
      ? `${formatTRY(discount.discountedGross, 2)} · %${discount.discountRate}`
      : formatTRY(discount.discountedGross, 2);

  return (
    <div className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-default w-full max-w-[300px] mx-auto">
      <div className="relative aspect-video overflow-hidden bg-gray-100">
        {product.image && !imageError ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-300 animate-pulse flex items-center justify-center">
                <div className="text-gray-500 text-xs">Yükleniyor...</div>
              </div>
            )}
            <img
              alt={productName}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
              src={product.image}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm font-medium">Fotoğraf Yok</div>
        )}

        <div className={`absolute top-2 right-2 bg-white/95 text-gray-900 rounded-full shadow font-bold whitespace-nowrap text-center ${
          product.showOffersInsteadOfPrice
            ? "max-w-[145px] px-4 py-2 text-sm"
            : `max-w-[105px] ${getPreviewPriceTextClass(priceText)}`
        }`}>
          {priceText}
        </div>
      </div>

      <div className="px-3 py-2 h-[72px] flex flex-col justify-start overflow-hidden">
        <h3 title={productName} className="text-xs leading-tight line-clamp-2 min-h-[28px] font-bold text-gray-900 mb-0.5 group-hover:text-emerald-600 transition-colors break-words">
          {productName}
        </h3>
        <p title={productDescription} className="text-[10px] leading-tight line-clamp-2 text-gray-600 break-words">
          {productDescription}
        </p>
      </div>
    </div>
  );
};

const ProductVideoEditor = ({ videoUrl, videoId, languageCode, uploading, onUpload }) => (
  <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
    <div className="mb-2 flex items-center justify-between gap-3">
      <div>
        <h3 className="text-sm font-bold text-purple-900">{getProductLanguageLabel(languageCode)} Videosu</h3>
        <p className="text-[11px] text-purple-700">Video bu dile özeldir ve 16:9 gösterilir.</p>
      </div>
      <label className={`rounded-lg px-3 py-2 text-xs font-bold text-white ${uploading ? "cursor-wait bg-purple-300" : "cursor-pointer bg-purple-600 hover:bg-purple-700"}`}>
        {uploading ? "Yükleniyor..." : videoUrl ? "Videoyu Değiştir" : "Video Yükle"}
        <input type="file" accept="video/*" className="hidden" disabled={uploading} onChange={onUpload} />
      </label>
    </div>
    <div className="relative aspect-video overflow-hidden rounded-lg bg-slate-900">
      {videoUrl ? (
        <video key={videoUrl} src={videoUrl} className="h-full w-full object-contain" controls preload="metadata" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white/75">
          <span className="text-3xl">🎬</span>
          <span className="mt-1 text-xs">Bu dil için video yüklenmedi</span>
        </div>
      )}
    </div>
    <div className="mt-2 rounded-lg border border-purple-200 bg-white px-3 py-2 text-[11px] text-purple-900">
      <span className="font-bold">Video No:</span>{" "}
      <span className="font-mono break-all">{videoId || "Video yüklendiğinde 30 haneli numara oluşturulur"}</span>
    </div>
  </div>
);


const DepartmentSelect = ({
  label = "Departman",
  value,
  departments = [],
  onChange,
  size = "md",
  helperText = "",
}) => {
  const safeValue = normalizeDepartmentNo(value);
  const hasDepartments = Array.isArray(departments) && departments.length > 0;
  const selectClass =
    size === "sm"
      ? "w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      : "w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={safeValue}
        onChange={(e) => onChange(normalizeDepartmentNo(e.target.value))}
        disabled={!hasDepartments}
        className={`${selectClass} ${!hasDepartments ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
      >
        <option value="">{hasDepartments ? "Departman seçin" : "Önce Fiş Bilgileri sayfasından departman ekleyin"}</option>
        {departments.map((department) => (
          <option key={department.number} value={department.number}>
            {department.label}
          </option>
        ))}
      </select>
      {helperText && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
    </div>
  );
};

const CategoryImage = ({ src, alt }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(!src);

  useEffect(() => {
    if (src) {
      setImageError(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
      {!imageError && src ? (
        <div className="w-12 h-12 relative">
          {!imageLoaded && <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg"></div>}
          <img
            src={src}
            alt={alt}
            className={`w-12 h-12 object-cover rounded-lg transition-opacity duration-300 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="text-gray-500 text-xs">Fotoğraf Yok</div>
      )}
    </div>
  );
};

const ProductListImage = ({ src, alt }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(!src);

  useEffect(() => {
    if (src) {
      setImageError(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
      {!imageError && src ? (
        <div className="w-16 h-16 relative">
          {!imageLoaded && <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg"></div>}
          <img
            src={src}
            alt={alt}
            className={`w-16 h-16 object-cover rounded-lg transition-opacity duration-300 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="text-gray-400 text-xs text-center">
          <span>Fotoğraf Yok</span>
        </div>
      )}
    </div>
  );
};

const normalizeProduct = (p) => {
  const baseRaw = parseNumLoose(p?.basePrice) ?? parseNumLoose(p?.price) ?? 0;
  const base = round2(Number(baseRaw || 0));
  const kdv = Number.isFinite(Number(p?.kdv)) ? Number(p.kdv) : 10;
  const departmanno = normalizeDepartmentNo(p?.departmanno ?? p?.departmentNo ?? p?.departmentno ?? "");
  const localizedData = normalizeProductLocalizedData(p);

  return syncProductCompatibilityFields({
    ...p,
    // Eski kayıtlardaki üst seviye fiyat/departman Türkçe locale oluşturulurken kullanılır.
    basePrice: base,
    kdv,
    departmanno,
    image: String(p?.image || p?.coverPhotoUrl || ""),
    sharedTimeValue: normalizeStandardDurationValue(p?.sharedTimeValue ?? p?.sharedTime),
    showOffersInsteadOfPrice: !!p?.showOffersInsteadOfPrice,
    menuProductIsMain: !!p?.menuProductIsMain,
    addMainProductToReceipt: !!p?.addMainProductToReceipt && !p?.menuProductIsMain,
    showRecommendedProducts: !!p?.showRecommendedProducts,
    localizedData,
  });
};


const CategoryLanguageEditor = ({
  localizedData,
  activeLanguage,
  onActiveLanguageChange,
  onLabelChange,
  onAddLanguage,
  onDeleteLanguage,
  disabled = false,
}) => {
  const [languageToAdd, setLanguageToAdd] = useState("");
  const normalized = normalizeCategoryLocalizedData({ localizedData });
  const active = getCategoryLanguageData({ localizedData: normalized }, activeLanguage);
  const usedCodes = new Set(normalized.map((item) => item.languageCode));
  const availableLanguages = PRODUCT_LANGUAGE_OPTIONS.filter((option) => !usedCodes.has(option.code));

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {normalized.map((locale) => (
          <div key={locale.languageCode} className="inline-flex overflow-hidden rounded-lg border border-indigo-200 bg-white">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onActiveLanguageChange(locale.languageCode)}
              className={`px-3 py-2 text-xs font-bold ${activeLanguage === locale.languageCode ? "bg-indigo-600 text-white" : "text-indigo-800 hover:bg-indigo-100"}`}
            >
              {getProductLanguageLabel(locale.languageCode)}
            </button>
            {locale.languageCode !== "tr" && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onDeleteLanguage(locale.languageCode)}
                className="border-l border-indigo-200 px-2 text-xs font-bold text-red-600 hover:bg-red-50"
                title="Bu dili sil"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      <label className="mb-2 block text-sm font-medium text-gray-700">
        Kategori Adı · {getProductLanguageLabel(activeLanguage)} *
      </label>
      <input
        type="text"
        value={active.label || ""}
        onChange={(event) => onLabelChange(activeLanguage, event.target.value)}
        placeholder={`${getProductLanguageLabel(activeLanguage)} kategori adını girin...`}
        className="w-full rounded-lg border border-indigo-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        disabled={disabled}
        maxLength={100}
      />

      {availableLanguages.length > 0 && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <select
            value={languageToAdd}
            onChange={(event) => setLanguageToAdd(event.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm"
            disabled={disabled}
          >
            <option value="">Dil seçin...</option>
            {availableLanguages.map((option) => (
              <option key={option.code} value={option.code}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={disabled || !languageToAdd}
            onClick={() => {
              if (!languageToAdd) return;
              onAddLanguage(languageToAdd);
              onActiveLanguageChange(languageToAdd);
              setLanguageToAdd("");
            }}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${disabled || !languageToAdd ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
          >
            + Dil Ekle
          </button>
        </div>
      )}
      <p className="mt-2 text-xs text-indigo-700">Kategori ID ve bağlantısı bütün dillerde aynıdır; yalnızca görünen kategori adı değişir.</p>
    </div>
  );
};

const MerchantProductManagement = () => {

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [activeProductLanguage, setActiveProductLanguage] = useState("tr");
  const [productVideoUploading, setProductVideoUploading] = useState(false);

  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [textLimitWarnings, setTextLimitWarnings] = useState({});

  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryData, setEditingCategoryData] = useState({
    id: "", label: "", icon: "", oldLabel: "", sira: 1,
    languages: ["tr"], localizedData: [createEmptyCategoryLanguageData("tr")],
  });
  const [activeNewCategoryLanguage, setActiveNewCategoryLanguage] = useState("tr");
  const [activeEditCategoryLanguage, setActiveEditCategoryLanguage] = useState("tr");

  const [newQuestion, setNewQuestion] = useState({ text: "", type: "multiple", isRequired: false });
  const [newCategory, setNewCategory] = useState({
    label: "", icon: "", iconFile: null,
    languages: ["tr"], localizedData: [createEmptyCategoryLanguageData("tr")],
  });

  const [activeQuestion, setActiveQuestion] = useState(0);
  const [priceErrors, setPriceErrors] = useState({});

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasCategoryUnsavedChanges, setHasCategoryUnsavedChanges] = useState(false);

  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const [pendingProductIndex, setPendingProductIndex] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingRedirect, setPendingRedirect] = useState(null);

  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const [showSortCategoryModal, setShowSortCategoryModal] = useState(false);
  const [categoryToSort, setCategoryToSort] = useState(null);
  const [sortOrderValue, setSortOrderValue] = useState("");

  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const [allProductsData, setAllProductsData] = useState({});
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  const [showDeletingProductModal, setShowDeletingProductModal] = useState(false);
  const [showProductDeletedModal, setShowProductDeletedModal] = useState(false);

  const questionRefs = useRef([]);
  const questionsSectionRef = useRef(null);
  const pendingQuestionScrollRef = useRef(null);
  const lastSyncedCategoryLiabilityRef = useRef(null);

  const scrollToQuestion = (questionIndex, behavior = "smooth") => {
    const safeIndex = Number(questionIndex);
    if (!Number.isInteger(safeIndex) || safeIndex < 0) return;

    const runScroll = () => {
      const target = questionRefs.current[safeIndex];

      if (target) {
        target.scrollIntoView({
          behavior,
          block: "start",
          inline: "nearest",
        });
        return;
      }

      if (questionsSectionRef.current) {
        questionsSectionRef.current.scrollIntoView({
          behavior,
          block: "start",
          inline: "nearest",
        });
      }
    };

    // React state güncellendikten ve DOM yeni soru kartını çizdikten sonra kaydır.
    const requestFrame =
      typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame.bind(window)
        : (callback) => setTimeout(callback, 0);

    requestFrame(() => {
      requestFrame(runScroll);
    });
  };


  const getCategoryProducts = (category) => {
    if (!category) return [];
    const arr = allProductsData[category.liability] || [];
    return arr.filter((p) => p?.liability === category.liability).map((p) => normalizeProduct(p));
  };

  const getCategoryProductCount = (category) => getCategoryProducts(category).length;

  useEffect(() => {
    const token = getAuthToken();
    const card = getAuthCard();
    if (!token || !card) {
      redirectToLogin();
      return;
    }
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setInitialLoading(true);
      setError(null);

      const [categoriesResponse, fisResponse] = await Promise.all([
        apiService.getCategories(),
        apiService.getFisBilgi().catch((departmentError) => {
          console.warn("Departman bilgileri alınamadı:", departmentError);
          return null;
        }),
      ]);

      setDepartments(parseDepartments(fisResponse?.data?.departments || []));

      if (categoriesResponse.success) {
        const categoriesData = normalizeAndSortCategories(categoriesResponse.categories || []);
        setCategories(categoriesData);
        setOriginalCategories(JSON.parse(JSON.stringify(categoriesData)));

        if (categoriesData.length > 0) {
          const allProducts = await apiService.getAllProducts(categoriesData);

          const normalizedAll = {};
          Object.keys(allProducts).forEach((liab) => {
            normalizedAll[liab] = (allProducts[liab] || []).map((p) => normalizeProduct(p));
          });

          setAllProductsData(normalizedAll);
          await preloadAllImages(normalizedAll, categoriesData);
          setImagesPreloaded(true);

          const firstCategory = categoriesData[0];
          setSelectedCategory(firstCategory);

          const firstCatProducts = (normalizedAll[firstCategory.liability] || []).filter(
            (p) => p?.liability === firstCategory.liability
          );

          setProducts(firstCatProducts);
          setOriginalProducts(JSON.parse(JSON.stringify(firstCatProducts)));
        }

        setInitialLoading(false);
      } else {
        throw new Error(categoriesResponse.message || "Kategoriler çekilemedi");
      }
    } catch (err) {
      setError(err.message);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      const freshSelectedCategory =
        categories.find((category) => category.liability === selectedCategory.liability) || selectedCategory;

      if (freshSelectedCategory !== selectedCategory) {
        setSelectedCategory(freshSelectedCategory);
        return;
      }

      const catProducts = getCategoryProducts(freshSelectedCategory);
      const categoryChanged =
        lastSyncedCategoryLiabilityRef.current !== freshSelectedCategory.liability;

      const currentProductKey = categoryChanged
        ? ""
        : getProductIdentity(filteredProducts[selectedProduct] || products[selectedProduct]);

      let nextSelectedIndex = 0;
      if (!categoryChanged && currentProductKey) {
        const foundIndex = catProducts.findIndex(
          (product) => getProductIdentity(product) === currentProductKey
        );
        nextSelectedIndex = foundIndex >= 0 ? foundIndex : Math.min(selectedProduct, Math.max(0, catProducts.length - 1));
      }

      setProducts(catProducts);
      setOriginalProducts(JSON.parse(JSON.stringify(catProducts)));
      setSelectedProduct(nextSelectedIndex);
      if (categoryChanged) setActiveQuestion(0);

      lastSyncedCategoryLiabilityRef.current = freshSelectedCategory.liability;
    }
  }, [selectedCategory, categories, allProductsData]);

  const filteredProducts = products.filter((product) => product.liability === selectedCategory?.liability);
  const currentBaseProduct = filteredProducts[selectedProduct] || filteredProducts[0];
  const currentLanguageData = currentBaseProduct
    ? getProductLanguageData(currentBaseProduct, activeProductLanguage) || createEmptyProductLanguageData(activeProductLanguage)
    : null;
  const currentProduct = currentBaseProduct && currentLanguageData
    ? {
        ...currentBaseProduct,
        ...currentLanguageData,
        languageCode: activeProductLanguage,
        localizedData: currentBaseProduct.localizedData,
        image: currentBaseProduct.image || "",
      }
    : null;
  const currentCategory = selectedCategory;

  useEffect(() => {
    if (!currentBaseProduct) return;
    const supported = (currentBaseProduct.localizedData || []).map((item) => normalizeProductLanguageCode(item?.languageCode));
    const nextLanguage = supported.includes("tr") ? "tr" : supported[0] || "tr";
    setActiveProductLanguage(nextLanguage);
    setEditingField(null);
    setTempValue("");
    setActiveQuestion(0);
  }, [getProductIdentity(currentBaseProduct)]);

  const normalizeDepartmentFieldsForSave = (product) => {
    if (!product) return product;

    const localizedData = normalizeProductLocalizedData(product).map((locale) => ({
      ...locale,
      departmanno: normalizeDepartmentNo(locale.departmanno),
      questions: normalizeQuestionsForProductLanguage(locale.questions, !!locale.dayEnabled).map((question) => ({
        ...question,
        options: Array.isArray(question?.options)
          ? question.options.map((option) => ({
              ...option,
              departmanno: normalizeDepartmentNo(option?.departmanno),
            }))
          : [],
      })),
    }));

    return syncProductCompatibilityFields({
      ...product,
      localizedData,
    });
  };


  useEffect(() => {
    const questionCount = currentProduct?.questions?.length || 0;
    questionRefs.current = questionRefs.current.slice(0, questionCount);

    if (pendingQuestionScrollRef.current === null) return;

    const targetIndex = pendingQuestionScrollRef.current;
    pendingQuestionScrollRef.current = null;
    scrollToQuestion(targetIndex);
  }, [currentProduct?.id, currentProduct?.questions?.length]);

  useEffect(() => {
    const productsChanged = JSON.stringify(products) !== JSON.stringify(originalProducts);
    setHasUnsavedChanges(productsChanged);
  }, [products, originalProducts]);

  useEffect(() => {
    const categoriesChanged = JSON.stringify(categories) !== JSON.stringify(originalCategories);
    setHasCategoryUnsavedChanges(categoriesChanged);
  }, [categories, originalCategories]);

  const setCurrentProductGross = (gross) => {
    setCurrentProductFieldDraft("basePrice", round2(Number(gross || 0)));
  };

  const setCurrentProductVat = (rate) => {
    setCurrentProductFieldDraft("kdv", Number(rate));
  };

  const setCurrentProductDepartmentNo = (departmentNo) => {
    setCurrentProductFieldDraft("departmanno", normalizeDepartmentNo(departmentNo));
  };


  const setOptionGross = (questionIndex, optionIndex, gross) => {
    const g = round2(Number(gross || 0));
    setOptionFieldDraft(questionIndex, optionIndex, "price", g);
  };

  const setOptionVat = (questionIndex, optionIndex, vatRate) => {
    setOptionFieldDraft(questionIndex, optionIndex, "kdvoption", Number(vatRate));
  };

  const setOptionDepartmentNo = (questionIndex, optionIndex, departmentNo) => {
    setOptionFieldDraft(questionIndex, optionIndex, "departmanno", normalizeDepartmentNo(departmentNo));
  };


  const updateCategoryLocaleState = (setter, languageCode, label) => {
    const code = normalizeProductLanguageCode(languageCode);
    setter((previous) => {
      const normalized = normalizeCategoryLocalizedData(previous);
      const index = normalized.findIndex((item) => item.languageCode === code);
      const nextLocale = {
        ...(index >= 0 ? normalized[index] : createEmptyCategoryLanguageData(code)),
        languageCode: code,
        language: getProductLanguageLabel(code),
        label: String(label || "").slice(0, 100),
      };
      const nextLocalizedData = [...normalized];
      if (index >= 0) nextLocalizedData[index] = nextLocale;
      else nextLocalizedData.push(nextLocale);
      return syncCategoryCompatibilityFields({ ...previous, localizedData: nextLocalizedData });
    });
  };

  const addCategoryLocaleState = (setter, languageCode) => {
    const code = normalizeProductLanguageCode(languageCode);
    setter((previous) => {
      const normalized = normalizeCategoryLocalizedData(previous);
      if (normalized.some((item) => item.languageCode === code)) return previous;
      return syncCategoryCompatibilityFields({
        ...previous,
        localizedData: [...normalized, createEmptyCategoryLanguageData(code)],
      });
    });
  };

  const deleteCategoryLocaleState = (setter, languageCode, setActiveLanguage) => {
    const code = normalizeProductLanguageCode(languageCode);
    if (code === "tr") return;
    setter((previous) => {
      const nextLocalizedData = normalizeCategoryLocalizedData(previous).filter((item) => item.languageCode !== code);
      return syncCategoryCompatibilityFields({ ...previous, localizedData: nextLocalizedData });
    });
    setActiveLanguage((current) => current === code ? "tr" : current);
  };

  const validateCategoryLocalizedNames = (categoryData, ignoredLiability = "") => {
    const localizedData = normalizeCategoryLocalizedData(categoryData);
    const emptyLocale = localizedData.find((item) => !String(item.label || "").trim());
    if (emptyLocale) {
      return `${getProductLanguageLabel(emptyLocale.languageCode)} kategori adını girin`;
    }

    for (const locale of localizedData) {
      const duplicate = categories.find((category) => {
        if (ignoredLiability && category.liability === ignoredLiability) return false;
        const other = getCategoryLanguageData(category, locale.languageCode);
        return String(other?.label || "").trim().toLocaleLowerCase("tr") === String(locale.label || "").trim().toLocaleLowerCase("tr");
      });
      if (duplicate) return `${getProductLanguageLabel(locale.languageCode)} dilinde “${locale.label}” isimli kategori zaten mevcut`;
    }
    return "";
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    const normalizedCategory = syncCategoryCompatibilityFields(category);
    setEditingCategoryData({
      ...normalizedCategory,
      id: category.id,
      label: normalizedCategory.label,
      icon: category.icon,
      oldLabel: category.label,
      sira: normalizeCategoryOrderValue(category.sira, 1),
    });
    setActiveEditCategoryLanguage("tr");
    setShowEditCategoryModal(true);
  };

  const handleOpenSortCategory = (category) => {
    setCategoryToSort(category);
    setSortOrderValue(String(normalizeCategoryOrderValue(category?.sira, 1)));
    setShowSortCategoryModal(true);
  };

  const handleCloseSortCategoryModal = () => {
    setShowSortCategoryModal(false);
    setCategoryToSort(null);
    setSortOrderValue("");
  };

  const handleSortCategorySave = async () => {
    if (!categoryToSort) return;

    const desiredOrder = normalizeCategoryOrderValue(sortOrderValue, categoryToSort.sira || 1);
    const sortedCategories = normalizeAndSortCategories(categories);

    if (desiredOrder < 1 || desiredOrder > sortedCategories.length) {
      setError(`Lütfen 1 ile ${sortedCategories.length} arasında bir sıra girin`);
      return;
    }

    const targetCategory = sortedCategories[desiredOrder - 1];
    if (!targetCategory) {
      setError("Hedef sıra bulunamadı");
      return;
    }

    const currentIndex = sortedCategories.findIndex((item) => item.liability === categoryToSort.liability);
    if (currentIndex === desiredOrder - 1) {
      handleCloseSortCategoryModal();
      return;
    }

    const previousCategories = JSON.parse(JSON.stringify(categories));
    const fallbackCategories = buildSwappedCategoriesByDisplayOrder(categories, categoryToSort.liability, desiredOrder);

    try {
      setSaving(true);
      setError(null);

      // ✅ Gerçek kayıt burada yapılır.
      // Backend itemlist collection içinde bu kategoriyi hedef sıra ile yer değiştirir.
      const response = await apiService.swapCategoryOrder({
        liability: categoryToSort.liability,
        targetSira: desiredOrder,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Kategori sırası kaydedilemedi");
      }

      // Backend yeni kategorileri döndürürse onu kullan, dönmezse frontend hesapladığı fallback sıralamayı kullan.
      const nextCategories = normalizeAndSortCategories(
        Array.isArray(response.categories) && response.categories.length > 0 ? response.categories : fallbackCategories
      );

      setCategories(nextCategories);
      setOriginalCategories(JSON.parse(JSON.stringify(nextCategories)));

      if (selectedCategory?.liability) {
        const updatedSelected = nextCategories.find((item) => item.liability === selectedCategory.liability);
        if (updatedSelected) setSelectedCategory(updatedSelected);
      }

      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
      handleCloseSortCategoryModal();
    } catch (err) {
      setCategories(previousCategories);
      setOriginalCategories(JSON.parse(JSON.stringify(previousCategories)));
      setError("Kategori sıralanırken hata oluştu: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategorySave = async () => {
    if (!editingCategory) return;

    const normalizedCategoryData = syncCategoryCompatibilityFields(editingCategoryData);
    const validationError = validateCategoryLocalizedNames(normalizedCategoryData, editingCategory.liability);
    if (validationError) {
      setError(validationError);
      return;
    }

    const newCategoryName = normalizedCategoryData.label.trim();

    try {
      setSaving(true);
      setError(null);

      const updateData = {
        liability: editingCategory.liability,
        id: editingCategory.id,
        label: newCategoryName,
        languages: normalizedCategoryData.languages,
        localizedData: normalizedCategoryData.localizedData,
        icon: normalizedCategoryData.icon,
        oldLabel: editingCategoryData.oldLabel,
        sira: normalizeCategoryOrderValue(normalizedCategoryData.sira, editingCategory.sira || 1),
      };

      const response = await apiService.updateCategory(updateData);

      if (response.success) {
        const responseCategory = syncCategoryCompatibilityFields(response.category || updateData);
        const updatedCategories = normalizeAndSortCategories(
          categories.map((cat) =>
            cat.liability === editingCategory.liability
              ? {
                  ...cat,
                  ...responseCategory,
                  id: editingCategory.id,
                  icon: responseCategory.icon || normalizedCategoryData.icon,
                  sira: normalizeCategoryOrderValue(normalizedCategoryData.sira, cat.sira || 1),
                }
              : cat
          )
        );

        setCategories(updatedCategories);
        setOriginalCategories(JSON.parse(JSON.stringify(updatedCategories)));

        const updatedAllProducts = { ...allProductsData };
        if (updatedAllProducts[editingCategory.liability]) {
          const updatedProductsArr = updatedAllProducts[editingCategory.liability].map((product) => ({
            ...product,
            category: newCategoryName,
          }));
          updatedAllProducts[editingCategory.liability] = updatedProductsArr;
          setAllProductsData(updatedAllProducts);

          if (selectedCategory?.liability === editingCategory.liability) {
            const safe = updatedProductsArr.filter((p) => p?.liability === editingCategory.liability);
            setProducts(safe);
            setOriginalProducts(JSON.parse(JSON.stringify(safe)));
          }
        }

        if (selectedCategory?.liability === editingCategory.liability) {
          setSelectedCategory(updatedCategories.find((cat) => cat.liability === editingCategory.liability));
        }

        setShowEditCategoryModal(false);
        setEditingCategory(null);
        setEditingCategoryData({
          id: "", label: "", icon: "", oldLabel: "", sira: 1,
          languages: ["tr"], localizedData: [createEmptyCategoryLanguageData("tr")],
        });

        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);
      } else {
        throw new Error(response.message || "Kategori güncellenirken hata oluştu");
      }
    } catch (error) {
      setError("Kategori güncellenirken hata oluştu: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategoryCancel = () => {
    setShowEditCategoryModal(false);
    setEditingCategory(null);
    setEditingCategoryData({
      id: "", label: "", icon: "", oldLabel: "", sira: 1,
      languages: ["tr"], localizedData: [createEmptyCategoryLanguageData("tr")],
    });
  };

  const saveCurrentProducts = async () => {
    if (!currentProduct || !currentCategory) return false;

    try {
      setSaving(true);
      setError(null);

      const productsWithDepartmentDefaults = products.map((product) =>
        product.liability === currentCategory.liability ? normalizeDepartmentFieldsForSave(product) : product
      );
      const productsToSave = productsWithDepartmentDefaults.filter(
        (product) => product.liability === currentCategory.liability
      );

      const updatedProductsToSend = productsToSave.map((product) => ({
        ...product,
        card: currentCategory.card,
        liability: currentCategory.liability,
        category: currentCategory.label,
        hasQuestions: (product.localizedData || []).some((locale) => Array.isArray(locale?.questions) && locale.questions.length > 0),
      }));

      const response = await apiService.saveProducts(updatedProductsToSend);

      if (response.success) {
        const selectedProductKey = getProductIdentity(currentProduct);
        const nextSelectedIndex = selectedProductKey
          ? productsWithDepartmentDefaults.findIndex(
              (product) => getProductIdentity(product) === selectedProductKey
            )
          : selectedProduct;

        setProducts(productsWithDepartmentDefaults);

        const updatedOriginalProducts = JSON.parse(JSON.stringify(productsWithDepartmentDefaults));
        setOriginalProducts(updatedOriginalProducts);

        setAllProductsData((prev) => ({
          ...prev,
          [currentCategory.liability]: productsToSave,
        }));

        if (nextSelectedIndex >= 0) setSelectedProduct(nextSelectedIndex);
        setHasUnsavedChanges(false);

        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);

        return true;
      } else {
        throw new Error(response.message || "Ürünler kaydedilirken hata oluştu");
      }
    } catch (error) {
      setError("Ürünler kaydedilirken hata oluştu: " + error.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    await saveCurrentProducts();
  };

  const handleSaveSingleProduct = async (product) => {
    if (!product || !currentCategory) return false;

    try {
      setSaving(true);
      setError(null);

      const productWithDepartmentDefaults = normalizeDepartmentFieldsForSave(product);
      const hasQuestions = (productWithDepartmentDefaults.localizedData || []).some(
        (locale) => Array.isArray(locale?.questions) && locale.questions.length > 0
      );
      const productToSave = {
        ...productWithDepartmentDefaults,
        card: currentCategory.card,
        liability: currentCategory.liability,
        category: currentCategory.label,
        hasQuestions,
      };

      const response = await apiService.saveProduct(productToSave);

      if (response.success) return true;
      throw new Error(response.message || "Ürün kaydedilirken hata oluştu");
    } catch (error) {
      setError("Ürün kaydedilirken hata oluştu: " + error.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const clearTextLimitWarning = (field) => {
    setTextLimitWarnings((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const setTextLimitWarning = (field, message) => {
    setTextLimitWarnings((prev) => ({ ...prev, [field]: message }));
  };

  const updateCurrentProductLanguage = (updater) => {
    if (!currentBaseProduct) return;
    setProducts((prev) => {
      const productIndex = prev.findIndex((p) => p.id === currentBaseProduct.id);
      if (productIndex === -1) return prev;
      const updatedProducts = [...prev];
      updatedProducts[productIndex] = updateProductLanguageData(updatedProducts[productIndex], activeProductLanguage, updater);
      return updatedProducts;
    });
  };

  const setCurrentProductFieldDraft = (field, value) => {
    if (!currentBaseProduct) return;
    const localizedFields = new Set([
      "name",
      "description",
      "basePrice",
      "kdv",
      "discountEnabled",
      "discountRate",
      "departmanno",
      "active",
      "videoUrl",
      "videoId",
      "videoDescription",
      "videoVersion",
      "videoUpdatedAt",
      "personnelEnabled",
      "timeEnabled",
      "dayEnabled",
    ]);

    if (localizedFields.has(field)) {
      updateCurrentProductLanguage((locale) => ({ ...locale, [field]: value }));
      return;
    }

    setProducts((prev) => {
      const productIndex = prev.findIndex((p) => p.id === currentBaseProduct.id);
      if (productIndex === -1) return prev;

      const current = prev[productIndex];
      // Bu iki ayar aynı anda açık olamaz. Menü ürünü ana ürün olduğunda
      // ayrıca ana ürünü fişe ekleme seçeneği kapalı kalır.
      if (field === "addMainProductToReceipt" && value === true && current.menuProductIsMain) {
        return prev;
      }

      const nextProduct = { ...current, [field]: value };
      if (field === "menuProductIsMain" && value === true) {
        nextProduct.addMainProductToReceipt = false;
      }
      if (field === "addMainProductToReceipt" && value === true) {
        nextProduct.menuProductIsMain = false;
      }

      if (
        current[field] === nextProduct[field] &&
        current.menuProductIsMain === nextProduct.menuProductIsMain &&
        current.addMainProductToReceipt === nextProduct.addMainProductToReceipt
      ) {
        return prev;
      }

      const updatedProducts = [...prev];
      updatedProducts[productIndex] = nextProduct;
      return updatedProducts;
    });
  };

  const setProductModeFlag = (field) => {
    updateCurrentProductLanguage((locale) => {
      const nextValue = !locale?.[field];
      const wasDayEnabled = !!locale?.dayEnabled;
      let next = { ...locale, [field]: nextValue };

      if (field === "dayEnabled") {
        next = {
          ...next,
          personnelEnabled: nextValue ? false : !!next.personnelEnabled,
          timeEnabled: nextValue ? false : !!next.timeEnabled,
          questions: normalizeQuestionsForProductLanguage(next.questions).map((question) => ({
            ...question,
            options: (question.options || []).map((option) => ({
              ...option,
              durationValue: option.durationEnabled
                ? normalizeDurationForDayMode(option.durationValue, nextValue)
                : "",
            })),
          })),
        };
      } else if (nextValue && wasDayEnabled) {
        next = {
          ...next,
          dayEnabled: false,
          questions: normalizeQuestionsForProductLanguage(next.questions).map((question) => ({
            ...question,
            options: (question.options || []).map((option) => ({
              ...option,
              durationValue: option.durationEnabled
                ? normalizeDurationForDayMode(option.durationValue, false)
                : "",
            })),
          })),
        };
      }

      return next;
    });
  };

  const setQuestionFieldDraft = (questionIndex, field, value) => {
    updateCurrentProductLanguage((locale) => {
      const questions = normalizeQuestionsForProductLanguage(locale.questions);
      if (!questions[questionIndex]) return locale;
      questions[questionIndex] = { ...questions[questionIndex], [field]: value };
      return { ...locale, questions };
    });
  };

  const setOptionFieldDraft = (questionIndex, optionIndex, field, value) => {
    updateCurrentProductLanguage((locale) => {
      const questions = normalizeQuestionsForProductLanguage(locale.questions);
      const question = questions[questionIndex];
      if (!question) return locale;
      const options = Array.isArray(question.options) ? [...question.options] : [];
      if (!options[optionIndex]) return locale;
      options[optionIndex] = { ...options[optionIndex], [field]: value };
      questions[questionIndex] = { ...question, options };
      return { ...locale, questions };
    });
  };


  const handleTempValueChange = (rawValue) => {
    const rawText = String(rawValue ?? "");

    if (editingField === "name") {
      const nextValue = getLimitedText(rawText, PRODUCT_NAME_MAX_LENGTH);
      if (rawText.length > PRODUCT_NAME_MAX_LENGTH) {
        setTextLimitWarning("name", `${PRODUCT_NAME_MAX_LENGTH} karakterden fazla olamaz.`);
      } else {
        clearTextLimitWarning("name");
      }
      setTempValue(nextValue);
      setCurrentProductFieldDraft("name", nextValue);
      return;
    }

    if (editingField === "description") {
      const nextValue = getLimitedText(rawText, PRODUCT_DESCRIPTION_MAX_LENGTH);
      if (rawText.length > PRODUCT_DESCRIPTION_MAX_LENGTH) {
        setTextLimitWarning("description", `${PRODUCT_DESCRIPTION_MAX_LENGTH} karakterden fazla olamaz.`);
      } else {
        clearTextLimitWarning("description");
      }
      setTempValue(nextValue);
      setCurrentProductFieldDraft("description", nextValue);
      return;
    }

    const questionMatch = String(editingField || "").match(/^question-(\d+)-text$/);
    if (questionMatch) {
      const questionIndex = Number(questionMatch[1]);
      setTempValue(rawText);
      setQuestionFieldDraft(questionIndex, "text", rawText);
      return;
    }

    const optionMatch = String(editingField || "").match(/^option-(\d+)-(\d+)-name$/);
    if (optionMatch) {
      const questionIndex = Number(optionMatch[1]);
      const optionIndex = Number(optionMatch[2]);
      setTempValue(rawText);
      setOptionFieldDraft(questionIndex, optionIndex, "name", rawText);
      return;
    }

    setTempValue(rawText);
  };

  const updateProductField = (field, value) => {
    let nextValue = value;

    if (field === "name") {
      nextValue = getLimitedText(value, PRODUCT_NAME_MAX_LENGTH);
      clearTextLimitWarning("name");
    }

    if (field === "description") {
      nextValue = getLimitedText(value, PRODUCT_DESCRIPTION_MAX_LENGTH);
      clearTextLimitWarning("description");
    }

    setCurrentProductFieldDraft(field, nextValue);
    setTempValue(nextValue);
    setEditingField(null);
  };

  const updateQuestionField = (questionIndex, field, value) => {
    setQuestionFieldDraft(questionIndex, field, value);
    setEditingField(null);
  };

  const updateOptionField = (questionIndex, optionIndex, field, value) => {
    setOptionFieldDraft(questionIndex, optionIndex, field, value);
    setEditingField(null);
  };

  const startEditing = (field, value = "") => {
    setEditingField(field);
    if (field === "name") {
      clearTextLimitWarning("name");
      setTempValue(getLimitedText(value, PRODUCT_NAME_MAX_LENGTH));
      return;
    }
    if (field === "description") {
      clearTextLimitWarning("description");
      setTempValue(getLimitedText(value, PRODUCT_DESCRIPTION_MAX_LENGTH));
      return;
    }
    setTempValue(value);
  };

  const handleImageUpload = async (event, questionIndex, optionIndex, categoryId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      const maxSize = 8 * 1024 * 1024;
      if (file.size > maxSize) {
        setError("Dosya boyutu 8MB'dan küçük olmalıdır");
        setUploading(false);
        return;
      }

      const timestamp = Date.now();
      const fileExtension = file.name.split(".").pop();
      const fileName = `image_${timestamp}.${fileExtension}`;

      let folder = "uploads";
      if (categoryId !== undefined) folder = "categories";
      else if (questionIndex !== undefined && optionIndex !== undefined) folder = "options";
      else if (questionIndex !== undefined) folder = "questions";
      else folder = "products";

      const imageUrl = await apiService.uploadImageToR2(file, fileName, folder);

      let productUpdated = false;
      let updatedProduct = null;

      if (categoryId !== undefined) {
        const updatedCategories = [...categories];
        const categoryIndex = updatedCategories.findIndex((cat) => cat.id === categoryId);

        if (categoryIndex !== -1) {
          const category = updatedCategories[categoryIndex];
          updatedCategories[categoryIndex].icon = imageUrl;
          setCategories(normalizeAndSortCategories(updatedCategories));

          try {
            const updateData = {
              liability: category.liability,
              id: category.id,
              label: category.label,
              icon: imageUrl,
              oldLabel: category.label,
              sira: normalizeCategoryOrderValue(category.sira, categoryIndex + 1),
            };

            await apiService.updateCategory(updateData);

            const updatedOriginalCategories = [...originalCategories];
            const originalCategoryIndex = updatedOriginalCategories.findIndex((cat) => cat.id === categoryId);
            if (originalCategoryIndex !== -1) {
              updatedOriginalCategories[originalCategoryIndex].icon = imageUrl;
              setOriginalCategories(normalizeAndSortCategories(updatedOriginalCategories));
            }
          } catch (error) {
            updatedCategories[categoryIndex].icon = category.icon;
            setCategories(normalizeAndSortCategories(updatedCategories));
            setError("İkon güncellenirken hata oluştu: " + error.message);
          }
        }
      } else if (questionIndex !== undefined && optionIndex !== undefined) {
        const updatedProducts = [...products];
        const productIndex = updatedProducts.findIndex((p) => p.id === currentBaseProduct.id);
        if (productIndex !== -1) {
          updatedProducts[productIndex] = updateProductLanguageData(
            updatedProducts[productIndex],
            activeProductLanguage,
            (locale) => {
              const questions = normalizeQuestionsForProductLanguage(locale.questions);
              if (questions[questionIndex]?.options?.[optionIndex]) {
                const options = [...questions[questionIndex].options];
                options[optionIndex] = { ...options[optionIndex], image: imageUrl };
                questions[questionIndex] = { ...questions[questionIndex], options };
              }
              return { ...locale, questions };
            }
          );
          setProducts(updatedProducts);
          productUpdated = true;
          updatedProduct = updatedProducts[productIndex];
        }
      } else if (questionIndex !== undefined) {
        const updatedProducts = [...products];
        const productIndex = updatedProducts.findIndex((p) => p.id === currentBaseProduct.id);
        if (productIndex !== -1) {
          updatedProducts[productIndex] = updateProductLanguageData(
            updatedProducts[productIndex],
            activeProductLanguage,
            (locale) => {
              const questions = normalizeQuestionsForProductLanguage(locale.questions);
              if (questions[questionIndex]) questions[questionIndex] = { ...questions[questionIndex], image: imageUrl };
              return { ...locale, questions };
            }
          );
          setProducts(updatedProducts);
          productUpdated = true;
          updatedProduct = updatedProducts[productIndex];
        }
      } else {
        const updatedProducts = [...products];
        const productIndex = updatedProducts.findIndex((p) => p.id === currentBaseProduct.id);
        if (productIndex !== -1) {
          updatedProducts[productIndex] = { ...updatedProducts[productIndex], image: imageUrl };
          setProducts(updatedProducts);
          productUpdated = true;
          updatedProduct = updatedProducts[productIndex];
        }
      }

      if (productUpdated && updatedProduct) {
        await handleSaveSingleProduct(updatedProduct);
      }

      setUploading(false);
    } catch (error) {
      setError("Resim yüklenirken hata oluştu: " + error.message);
      setUploading(false);
    }
  };

  const handleProductImageUpload = async (event) => {
    await handleImageUpload(event);
  };


  const handleProductVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !currentBaseProduct) return;
    if (!String(file.type || "").startsWith("video/")) {
      setError("Lütfen geçerli bir video dosyası seçin");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError("Video boyutu 50 MB'dan küçük olmalıdır");
      return;
    }

    try {
      setProductVideoUploading(true);
      setError(null);
      const uploadedVideo = await apiService.uploadProductVideo(file, currentBaseProduct.uniqcode, activeProductLanguage);
      const videoUrl = uploadedVideo.url;
      const videoId = uploadedVideo.videoId;
      const nowIso = new Date().toISOString();
      const currentVersion = Math.max(1, Number(currentLanguageData?.videoVersion || 1));
      const updatedProduct = updateProductLanguageData(currentBaseProduct, activeProductLanguage, (locale) => ({
        ...locale,
        videoUrl,
        videoId,
        videoVersion: locale.videoUrl && locale.videoUrl !== videoUrl ? currentVersion + 1 : currentVersion,
        videoUpdatedAt: nowIso,
      }));

      setProducts((prev) => prev.map((item) => (item.id === currentBaseProduct.id ? updatedProduct : item)));
      const saved = await handleSaveSingleProduct(updatedProduct);
      if (saved) {
        setOriginalProducts((prev) => prev.map((item) => (item.id === currentBaseProduct.id ? JSON.parse(JSON.stringify(updatedProduct)) : item)));
        setAllProductsData((prev) => ({
          ...prev,
          [currentCategory.liability]: (prev[currentCategory.liability] || []).map((item) =>
            item.id === currentBaseProduct.id ? updatedProduct : item
          ),
        }));
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);
      }
    } catch (uploadError) {
      setError("Video yüklenirken hata oluştu: " + uploadError.message);
    } finally {
      setProductVideoUploading(false);
    }
  };

  const handleProductLanguageChange = (languageCode) => {
    const code = normalizeProductLanguageCode(languageCode);
    if (!currentBaseProduct) return;
    const exists = !!getProductLanguageData(currentBaseProduct, code);
    if (!exists) {
      setProducts((prev) =>
        prev.map((product) =>
          product.id === currentBaseProduct.id
            ? updateProductLanguageData(product, code, createEmptyProductLanguageData(code))
            : product
        )
      );
    }
    setActiveProductLanguage(code);
    setEditingField(null);
    setTempValue("");
    setActiveQuestion(0);
  };

  const removeActiveProductLanguage = () => {
    if (!currentBaseProduct || activeProductLanguage === "tr") return;
    setProducts((prev) =>
      prev.map((product) => {
        if (product.id !== currentBaseProduct.id) return product;
        const localizedData = normalizeProductLocalizedData(product).filter(
          (item) => normalizeProductLanguageCode(item?.languageCode) !== activeProductLanguage
        );
        return syncProductCompatibilityFields({ ...product, localizedData });
      })
    );
    setActiveProductLanguage("tr");
    setEditingField(null);
    setActiveQuestion(0);
  };

  const handleOptionImageUpload = async (event, questionIndex, optionIndex) => {
    await handleImageUpload(event, questionIndex, optionIndex);
  };

  const addNewProduct = () => {
    if (hasUnsavedChanges) {
      setPendingAction("new");
      setShowUnsavedChangesModal(true);
      return;
    }
    executeAddNewProduct();
  };

  const executeAddNewProduct = async () => {
    try {
      const newUniqcode = `uniq_${Date.now()}`;
      const card = getAuthCard();

      const newProductData = {
        card: card,
        category: currentCategory.label,
        name: "Yeni Ürün",
        price: "₺0",
        desc: "Ürün açıklaması",
        image: "",
        uniqcode: newUniqcode,
        hasQuestions: false,
        liability: currentCategory.liability,
        departmanno: "",
        sharedTimeValue: "",
        showOffersInsteadOfPrice: false,
        menuProductIsMain: false,
        addMainProductToReceipt: false,
        showRecommendedProducts: false,
        languages: ["tr"],
        localizedData: [
          {
            ...createEmptyProductLanguageData("tr"),
            name: "Yeni Ürün",
            description: "Ürün açıklaması",
          },
        ],
      };

      const addResponse = await apiService.addProduct(newProductData);

      if (addResponse.success) {
        const newProduct = normalizeProduct({
          id: Date.now().toString(),
          basePrice: 0,
          kdv: 10,
          image: "",
          name: "Yeni Ürün",
          description: "Ürün açıklaması",
          questions: [],
          active: true,
          liability: currentCategory.liability,
          category: currentCategory.label,
          uniqcode: newUniqcode,
          card: card,
          hasQuestions: false,
          departmanno: "",
          sharedTimeValue: "",
          showOffersInsteadOfPrice: false,
          menuProductIsMain: false,
          addMainProductToReceipt: false,
          showRecommendedProducts: false,
          languages: ["tr"],
          localizedData: [
            {
              ...createEmptyProductLanguageData("tr"),
              name: "Yeni Ürün",
              description: "Ürün açıklaması",
            },
          ],
        });

        const updatedProducts = [...products, newProduct];
        setProducts(updatedProducts);

        setAllProductsData((prev) => ({
          ...prev,
          [currentCategory.liability]: updatedProducts,
        }));

        setSelectedProduct(updatedProducts.length - 1);

        const success = await handleSaveSingleProduct(newProduct);
        if (success) {
          setOriginalProducts(JSON.parse(JSON.stringify(updatedProducts)));
          setHasUnsavedChanges(false);
          setShowSaveSuccess(true);
          setTimeout(() => setShowSaveSuccess(false), 3000);
        }
      } else {
        throw new Error(addResponse.message || "Ürün iteminfo'ya eklenirken hata oluştu");
      }
    } catch (error) {
      setError("Yeni ürün eklenirken hata oluştu: " + error.message);
    }
  };

  const removeProduct = async () => {
    if (currentProduct) {
      try {
        setSaving(true);
        setError(null);
        setShowDeletingProductModal(true);

        const response = await apiService.removeProduct(currentProduct.uniqcode);

        if (response.success) {
          const updatedProducts = products.filter((product) => product.id !== currentProduct.id);
          setProducts(updatedProducts);

          setAllProductsData((prev) => ({
            ...prev,
            [currentCategory.liability]: updatedProducts,
          }));

          if (filteredProducts.length === 1) {
            setSelectedProduct(0);
          } else if (selectedProduct >= filteredProducts.length - 1) {
            setSelectedProduct(Math.max(0, filteredProducts.length - 2));
          }

          setOriginalProducts(JSON.parse(JSON.stringify(updatedProducts)));
          setHasUnsavedChanges(false);

          setShowDeletingProductModal(false);
          setShowProductDeletedModal(true);
          setTimeout(() => setShowProductDeletedModal(false), 1500);
        } else {
          throw new Error(response.message || "Ürün silinirken hata oluştu");
        }
      } catch (error) {
        setError("Ürün silinirken hata oluştu: " + error.message);
        setShowDeletingProductModal(false);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleAddCategory = async () => {
    const normalizedCategoryData = syncCategoryCompatibilityFields(newCategory);
    const validationError = validateCategoryLocalizedNames(normalizedCategoryData);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!newCategory.iconFile) {
      setError("Lütfen bir kategori fotoğrafı seçin");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const file = newCategory.iconFile;
      const maxSize = 8 * 1024 * 1024;
      if (file.size > maxSize) {
        setError("Dosya boyutu 8MB'dan küçük olmalıdır");
        setUploading(false);
        return;
      }

      const timestamp = Date.now();
      const fileExtension = file.name.split(".").pop();
      const fileName = `category_${timestamp}.${fileExtension}`;
      const imageUrl = await apiService.uploadImageToR2(file, fileName, "categories");

      const categoryId = `category_${timestamp}`;
      const card = getAuthCard();
      const newCategoryObj = syncCategoryCompatibilityFields({
        id: categoryId,
        label: normalizedCategoryData.label,
        languages: normalizedCategoryData.languages,
        localizedData: normalizedCategoryData.localizedData,
        icon: imageUrl,
        liability: `liability_${timestamp}`,
        card,
        isitem: "menuItems",
        sira: getNextCategorySira(categories),
      });

      const response = await apiService.addCategory(newCategoryObj);
      if (!response?.success) throw new Error(response?.message || "Kategori eklenemedi");

      const savedCategory = syncCategoryCompatibilityFields(response.category || newCategoryObj);
      const updatedCategories = normalizeAndSortCategories([...categories, savedCategory]);
      setCategories(updatedCategories);
      setAllProductsData((prev) => ({ ...prev, [savedCategory.liability]: [] }));
      setOriginalCategories(JSON.parse(JSON.stringify(updatedCategories)));
      setHasUnsavedChanges(false);
      setHasCategoryUnsavedChanges(false);

      setShowAddCategoryModal(false);
      setNewCategory({
        label: "", icon: "", iconFile: null,
        languages: ["tr"], localizedData: [createEmptyCategoryLanguageData("tr")],
      });
      setActiveNewCategoryLanguage("tr");
      setSelectedCategory(updatedCategories.find((category) => category.liability === savedCategory.liability) || savedCategory);
      setSelectedProduct(0);

      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      setError("Kategori eklenemedi: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const removeCategory = async (categoryId) => {
    if (categories.length > 1) {
      try {
        setSaving(true);

        const categoryToRemove = categories.find((cat) => cat.id === categoryId);
        if (!categoryToRemove) throw new Error("Silinecek kategori bulunamadı");

        const response = await apiService.deleteCategory(categoryToRemove.liability);

        if (response.success) {
          const updatedCategories = normalizeAndSortCategories(categories.filter((cat) => cat.id !== categoryId));
          setCategories(updatedCategories);

          const updatedAllProducts = { ...allProductsData };
          delete updatedAllProducts[categoryToRemove.liability];
          setAllProductsData(updatedAllProducts);

          const updatedProducts = products.filter((product) => product.liability !== categoryToRemove.liability);
          setProducts(updatedProducts);

          setOriginalCategories(JSON.parse(JSON.stringify(updatedCategories)));
          setOriginalProducts(JSON.parse(JSON.stringify(updatedProducts)));

          setHasUnsavedChanges(false);
          setHasCategoryUnsavedChanges(false);

          if (selectedCategory?.id === categoryId) {
            setSelectedCategory(updatedCategories[0] || null);
            setSelectedProduct(0);
          }

          setShowDeleteSuccess(true);
          setTimeout(() => setShowDeleteSuccess(false), 3000);
        } else {
          throw new Error(response.message || "Kategori silinirken hata oluştu");
        }
      } catch (error) {
        setError("Kategori silinirken hata oluştu: " + error.message);
      } finally {
        setSaving(false);
        setShowDeleteCategoryModal(false);
        setCategoryToDelete(null);
      }
    }
  };

  const handleDeleteCategory = (categoryId) => {
    setCategoryToDelete(categoryId);
    setShowDeleteCategoryModal(true);
  };

  const confirmDeleteCategory = () => {
    if (categoryToDelete) removeCategory(categoryToDelete);
  };

  const cancelDeleteCategory = () => {
    setShowDeleteCategoryModal(false);
    setCategoryToDelete(null);
  };

  const toggleFlag = (flagType, questionIndex, optionIndex) => {
    if (questionIndex === undefined) {
      setCurrentProductFieldDraft(flagType, !currentProduct?.[flagType]);
      return;
    }
    updateCurrentProductLanguage((locale) => {
      const questions = normalizeQuestionsForProductLanguage(locale.questions);
      if (!questions[questionIndex]) return locale;
      if (optionIndex !== undefined) {
        const options = [...(questions[questionIndex].options || [])];
        if (!options[optionIndex]) return locale;
        options[optionIndex] = { ...options[optionIndex], [flagType]: !options[optionIndex][flagType] };
        questions[questionIndex] = { ...questions[questionIndex], options };
      } else {
        questions[questionIndex] = { ...questions[questionIndex], [flagType]: !questions[questionIndex][flagType] };
      }
      return { ...locale, questions };
    });
  };


  const handleProductSelect = (index) => {
    if (selectedProduct === index) return;

    if (hasUnsavedChanges) {
      setPendingProductIndex(index);
      setPendingAction("switch");
      setShowUnsavedChangesModal(true);
    } else {
      setSelectedProduct(index);
      setActiveProductLanguage("tr");
      setActiveQuestion(0);
    }
  };

  const handleCategorySelect = (category) => {
    if (selectedCategory?.id === category.id) return;

    if (hasUnsavedChanges) {
      setPendingAction("category_switch");
      setShowUnsavedChangesModal(true);

      setTimeout(() => {
        setSelectedCategory(category);
        setSelectedProduct(0);
      }, 100);
    } else {
      setSelectedCategory(category);
      setSelectedProduct(0);
      setActiveProductLanguage("tr");
      setActiveQuestion(0);
    }
  };

  const handleDesignPanelClick = () => {
    if (hasUnsavedChanges) {
      setPendingRedirect("/designpanel");
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/designpanel";
    }
  };

  const handleReceiptInfoClick = () => {
    if (hasUnsavedChanges) {
      setPendingRedirect("/ReceiptInfoPage");
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/ReceiptInfoPage";
    }
  };

  const handlePreferencesClick = () => {
    if (hasUnsavedChanges) {
      setPendingRedirect("/preferance");
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/preferance";
    }
  };

  const handleExcelFisClick = () => {
    if (hasUnsavedChanges) {
      setPendingRedirect("/excelfis");
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/excelfis";
    }
  };

  const handleBranchesClick = () => {
    if (hasUnsavedChanges) {
      setPendingRedirect("/branches");
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/branches";
    }
  };

  const handleAppointmentsClick = () => {
    if (hasUnsavedChanges) {
      setPendingRedirect("/appointments");
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/appointments";
    }
  };

  const getRedirectModalTitle = (path) => {
    if (path === "/designpanel") return "Dizayn Paneli'ne Geçiş";
    if (path === "/ReceiptInfoPage") return "Fiş Bilgi Sayfasına Geçiş";
    if (path === "/preferance") return "Tercihler Sayfasına Geçiş";
    if (path === "/excelfis") return "Excel Fiş Sayfasına Geçiş";
    if (path === "/branches") return "Şubeler Sayfasına Geçiş";
    if (path === "/appointments") return "Randevular Sayfasına Geçiş";
    return "Sayfaya Geçiş";
  };

  const getRedirectModalDesc = (path) => {
    if (path === "/designpanel")
      return "Kaydedilmemiş değişiklikleriniz var. Dizayn Paneli'ne geçmeden önce ne yapmak istiyorsunuz?";
    if (path === "/ReceiptInfoPage")
      return "Kaydedilmemiş değişiklikleriniz var. Fiş Bilgi sayfasına geçmeden önce ne yapmak istiyorsunuz?";
    if (path === "/preferance")
      return "Kaydedilmemiş değişiklikleriniz var. Tercihler sayfasına geçmeden önce ne yapmak istiyorsunuz?";
    if (path === "/excelfis")
      return "Kaydedilmemiş değişiklikleriniz var. Excel Fiş sayfasına geçmeden önce ne yapmak istiyorsunuz?";
    if (path === "/branches")
      return "Kaydedilmemiş değişiklikleriniz var. Şubeler sayfasına geçmeden önce ne yapmak istiyorsunuz?";
    if (path === "/appointments")
      return "Kaydedilmemiş değişiklikleriniz var. Randevular sayfasına geçmeden önce ne yapmak istiyorsunuz?";
    return "Kaydedilmemiş değişiklikleriniz var. Devam etmeden önce ne yapmak istiyorsunuz?";
  };

  const getRedirectSaveText = (path) => {
    if (path === "/designpanel") return "Değişiklikleri Kaydet ve Dizayn Paneli'ne Geç";
    if (path === "/ReceiptInfoPage") return "Değişiklikleri Kaydet ve Fiş Bilgi Sayfasına Geç";
    if (path === "/preferance") return "Değişiklikleri Kaydet ve Tercihler Sayfasına Geç";
    if (path === "/excelfis") return "Değişiklikleri Kaydet ve Excel Fiş Sayfasına Geç";
    if (path === "/branches") return "Değişiklikleri Kaydet ve Şubeler Sayfasına Geç";
    if (path === "/appointments") return "Değişiklikleri Kaydet ve Randevular Sayfasına Geç";
    return "Değişiklikleri Kaydet ve Devam Et";
  };

  const getRedirectNoSaveText = (path) => {
    if (path === "/designpanel") return "Kaydetmeden Dizayn Paneli'ne Geç";
    if (path === "/ReceiptInfoPage") return "Kaydetmeden Fiş Bilgi Sayfasına Geç";
    if (path === "/preferance") return "Kaydetmeden Tercihler Sayfasına Geç";
    if (path === "/excelfis") return "Kaydetmeden Excel Fiş Sayfasına Geç";
    if (path === "/branches") return "Kaydetmeden Şubeler Sayfasına Geç";
    if (path === "/appointments") return "Kaydetmeden Randevular Sayfasına Geç";
    return "Kaydetmeden Devam Et";
  };

  const executeProductSwitch = () => {
    setSelectedProduct(pendingProductIndex);
    setActiveProductLanguage("tr");
    setActiveQuestion(0);
  };

  const handleAddQuestion = () => {
    if (newQuestion.text.trim() && currentBaseProduct) {
      let newQuestionIndex = 0;
      updateCurrentProductLanguage((locale) => {
        const questions = normalizeQuestionsForProductLanguage(locale.questions);
        questions.push({
          ...newQuestion,
          hasQuantity: true,
          id: Date.now(),
          options: [
            {
              id: Date.now() + 1,
              name: "Yeni Seçenek",
              price: 0,
              kdvoption: 10,
              discountEnabled: false,
              discountRate: 0,
              departmanno: "",
              image: "",
              hasQuantity: false,
              durationEnabled: false,
              durationVisible: true,
              durationValue: "",
            },
          ],
        });
        newQuestionIndex = questions.length - 1;
        return { ...locale, questions };
      });
      setShowAddQuestionModal(false);
      setNewQuestion({ text: "", type: "multiple", isRequired: false });
      pendingQuestionScrollRef.current = newQuestionIndex;
      setActiveQuestion(newQuestionIndex);
    }
  };

  const addNewOption = (questionIndex) => {
    updateCurrentProductLanguage((locale) => {
      const questions = normalizeQuestionsForProductLanguage(locale.questions);
      if (!questions[questionIndex]) return locale;
      const options = [...(questions[questionIndex].options || [])];
      options.push({
        id: Date.now(),
        name: "Yeni Seçenek",
        price: 0,
        kdvoption: 10,
        discountEnabled: false,
        discountRate: 0,
        departmanno: "",
        image: "",
        hasQuantity: false,
        durationEnabled: false,
        durationVisible: true,
        durationValue: "",
      });
      questions[questionIndex] = { ...questions[questionIndex], options };
      return { ...locale, questions };
    });
  };

  const deleteOption = (questionIndex, optionIndex) => {
    updateCurrentProductLanguage((locale) => {
      const questions = normalizeQuestionsForProductLanguage(locale.questions);
      if (!questions[questionIndex]) return locale;
      const options = [...(questions[questionIndex].options || [])];
      options.splice(optionIndex, 1);
      questions[questionIndex] = { ...questions[questionIndex], options };
      return { ...locale, questions };
    });
  };

  const deleteQuestion = (questionIndex) => {
    updateCurrentProductLanguage((locale) => {
      const questions = normalizeQuestionsForProductLanguage(locale.questions);
      questions.splice(questionIndex, 1);
      return { ...locale, questions };
    });
    if (activeQuestion >= questionIndex) setActiveQuestion(Math.max(0, activeQuestion - 1));
  };


  const handleNavigationClick = (questionIndex) => {
    setActiveQuestion(questionIndex);
    scrollToQuestion(questionIndex);
  };
  const handleAddQuestionClick = () => setShowAddQuestionModal(true);

  const handleAddCategoryClick = () => {
    setNewCategory({
      label: "", icon: "", iconFile: null,
      languages: ["tr"], localizedData: [createEmptyCategoryLanguageData("tr")],
    });
    setActiveNewCategoryLanguage("tr");
    setShowAddCategoryModal(true);
  };

  const handleSaveAndContinue = async () => {
    try {
      if (hasUnsavedChanges) {
        const success = await saveCurrentProducts();
        if (!success) return;
      }

      if (pendingAction === "switch") executeProductSwitch();
      else if (pendingAction === "new") executeAddNewProduct();

      if (pendingRedirect) {
        setTimeout(() => (window.location.href = pendingRedirect), 20);
        return;
      }

      setShowUnsavedChangesModal(false);
      setPendingProductIndex(null);
      setPendingAction(null);
      setPendingRedirect(null);
    } catch (error) {
      setError("Değişiklikler kaydedilirken hata oluştu: " + error.message);
    }
  };

  const handleContinueWithoutSaving = () => {
    setShowUnsavedChangesModal(false);

    const action = pendingAction;
    const index = pendingProductIndex;
    const redirectUrl = pendingRedirect;

    setPendingProductIndex(null);
    setPendingAction(null);
    setPendingRedirect(null);

    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }

    const restoredProducts = JSON.parse(JSON.stringify(originalProducts));
    const restoredCategories = JSON.parse(JSON.stringify(originalCategories));

    setProducts(restoredProducts);
    setCategories(restoredCategories);

    setHasUnsavedChanges(false);
    setHasCategoryUnsavedChanges(false);

    setTimeout(() => {
      if (action === "switch") {
        setSelectedProduct(index);
        setActiveQuestion(0);
      } else if (action === "new") {
        executeAddNewProduct();
      } else if (action === "category_switch") {
        setActiveQuestion(0);
      }
    }, 100);
  };

  const handleDiscardChanges = () => {
    const restoredProducts = JSON.parse(JSON.stringify(originalProducts));
    const restoredCategories = JSON.parse(JSON.stringify(originalCategories));

    setProducts(restoredProducts);
    setCategories(restoredCategories);

    setHasUnsavedChanges(false);
    setHasCategoryUnsavedChanges(false);

    if (selectedProduct >= filteredProducts.length) {
      setSelectedProduct(Math.max(0, filteredProducts.length - 1));
    }
  };

  const handleCancelSwitch = () => {
    setShowUnsavedChangesModal(false);
    setPendingProductIndex(null);
    setPendingAction(null);
    setPendingRedirect(null);
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Sayfa yükleniyor...</p>
          <p className="text-sm text-gray-500 mt-2">Tüm kategoriler ve ürünler hazırlanıyor</p>
          <p className="text-xs text-gray-400 mt-1">Resimler ön yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error && categories.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Hata Oluştu</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadInitialData}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  const TOP_NAV_BTN =
    "inline-flex items-center justify-center w-full h-10 whitespace-nowrap text-white rounded-lg transition-colors text-sm font-semibold";

  const QUESTION_NAV_BTN =
    "w-[220px] h-[64px] px-4 py-3 rounded-xl transition-colors text-sm font-semibold text-center leading-snug whitespace-normal break-words overflow-hidden";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">

      <div className="w-full md:w-80 bg-white shadow-lg md:h-screen md:sticky md:top-0 flex flex-col overflow-hidden">
        <div className="p-4 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800">Urun Paneli</h2>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button onClick={handleDesignPanelClick} className={`${TOP_NAV_BTN} bg-purple-500 hover:bg-purple-600`}>
              Dizayn
            </button>

            <button onClick={handleReceiptInfoClick} className={`${TOP_NAV_BTN} bg-indigo-500 hover:bg-indigo-600`}>
              Fiş Bilgi
            </button>

            <button onClick={handlePreferencesClick} className={`${TOP_NAV_BTN} bg-teal-500 hover:bg-teal-600`}>
              Tercihler
            </button>

            <button onClick={handleExcelFisClick} className={`${TOP_NAV_BTN} bg-amber-500 hover:bg-amber-600`}>
              Excel Fiş
            </button>

            <button onClick={handleBranchesClick} className={`${TOP_NAV_BTN} bg-slate-800 hover:bg-slate-900`}>
              Şubeler
            </button>

            <button onClick={handleAppointmentsClick} className={`${TOP_NAV_BTN} bg-fuchsia-600 hover:bg-fuchsia-700`}>
              Randevular
            </button>
          </div>

          {hasUnsavedChanges && (
            <div className="mt-3 bg-yellow-100 border border-yellow-400 text-yellow-700 px-2 py-1 rounded text-xs">
              ⚠️ Kaydedilmemiş ürün değişiklikleri
            </div>
          )}

          {error && (
            <div className="mt-2 bg-red-100 border border-red-400 text-red-700 px-2 py-1 rounded text-xs">
              ❌ {error}
            </div>
          )}

          {showDeleteSuccess && (
            <div className="mt-2 bg-green-100 border border-green-400 text-green-700 px-2 py-1 rounded text-xs">
              ✅ Kategori başarıyla silindi
            </div>
          )}
        </div>

        <div className="p-4 flex-1 min-h-0 overflow-hidden flex flex-col">
          <div className="flex space-x-2 mb-4 flex-shrink-0">
            <button
              onClick={handleAddCategoryClick}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-lg transition-colors font-medium text-sm"
            >
              + Kategori Ekle
            </button>

            <button
              onClick={addNewProduct}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-lg transition-colors font-medium text-sm"
            >
              + Ürün Ekle
            </button>
          </div>

          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
            <div className="flex flex-col min-h-0 max-h-[40%] md:max-h-[45%] overflow-hidden">
              <h3 className="font-semibold text-gray-700 mb-3 flex-shrink-0">Kategoriler</h3>

              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                <div className="space-y-2 pr-1">
                  {categories.length > 0 ? (
                    categories.map((category, categoryIndex) => {
                      const count = getCategoryProductCount(category);
                      const displayOrder = normalizeCategoryOrderValue(category.sira, categoryIndex + 1);

                      return (
                        <div
                          key={category.id}
                          className={`p-3 rounded-lg cursor-pointer transition-all border ${
                            selectedCategory?.id === category.id
                              ? "bg-blue-50 border-blue-300 shadow-sm"
                              : "bg-white border-gray-200 hover:border-blue-200 hover:shadow-sm"
                          }`}
                          onClick={() => handleCategorySelect(category)}
                        >
                          {selectedCategory?.id === category.id ? (
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <CategoryImage src={category.icon} alt={category.label} />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-800 text-sm">{category.label}</h4>
                                    <p className="text-[11px] text-blue-700 mt-1 font-semibold">{displayOrder}. sıra</p>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-xs text-gray-500">{count} ürün</span>
                                  <div className="flex flex-wrap gap-2 justify-end">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSortCategory(category);
                                      }}
                                      className="text-amber-600 hover:text-amber-700 text-xs bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded"
                                    >
                                      ↕️ Sırala ({displayOrder})
                                    </button>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCategory(category);
                                      }}
                                      className="text-blue-500 hover:text-blue-700 text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded"
                                    >
                                      ✏️ Düzenle
                                    </button>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(category.id);
                                      }}
                                      className="text-red-500 hover:text-red-700 text-xs bg-red-50 hover:bg-red-100 px-2 py-1 rounded"
                                      disabled={categories.length <= 1}
                                    >
                                      🗑️ Sil
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-800 text-sm">{category.label}</h4>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="text-xs text-gray-500">
                                    {count} ürün • {displayOrder}. sıra
                                  </span>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSortCategory(category);
                                      }}
                                      className="text-amber-600 hover:text-amber-700 text-xs"
                                      title={`Sırala (${displayOrder}. sıra)`}
                                    >
                                      ↕️
                                    </button>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCategory(category);
                                      }}
                                      className="text-blue-500 hover:text-blue-700 text-xs"
                                    >
                                      ✏️
                                    </button>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(category.id);
                                      }}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                      disabled={categories.length <= 1}
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center p-4 text-gray-500 text-sm">Kategori bulunamadı</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <h3 className="font-semibold text-gray-700 mb-3 flex-shrink-0">
                {currentCategory?.label} Kategorisindeki Ürünler
              </h3>

              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                <div className="space-y-3 pr-1 pb-10">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product, index) => (
                      <div
                        key={product.id}
                        className={`p-3 rounded-lg cursor-pointer transition-all border ${
                          selectedProduct === index
                            ? "bg-blue-50 border-blue-300 shadow-sm"
                            : "bg-white border-gray-200 hover:border-blue-200 hover:shadow-sm"
                        }`}
                        onClick={() => handleProductSelect(index)}
                      >
                        <div className="flex items-center space-x-3">
                          <ProductListImage src={product.image} alt={product.name} />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-800 truncate text-sm">{product.name}</h4>
                                <p className="text-green-600 font-semibold text-sm">{formatTRY(product.basePrice || 0, 2)}</p>
                              </div>

                              <div
                                className={`w-2 h-2 rounded-full ${
                                  product.active !== false ? "bg-green-500" : "bg-red-500"
                                }`}
                              ></div>
                            </div>

                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-500">
                                {product.questions ? product.questions.length : 0} soru
                              </span>

                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  product.active !== false
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {product.active !== false ? "Aktif" : "Pasif"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-4 text-gray-500 text-sm">Bu kategoride ürün yok</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-6 overflow-auto">
        {currentProduct ? (
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between mb-6 space-y-4 lg:space-y-0">
                <div className="flex flex-col flex-1 gap-5">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-sm font-bold text-gray-800">Ortak Ürün Fotoğrafı</h3>
                        <p className="text-xs text-gray-500">Fotoğraf bütün dillerde aynıdır ve 16:9 gösterilir.</p>
                      </div>
                      <ProductImage
                        src={currentBaseProduct.image}
                        alt={currentProduct.name}
                        onImageChange={handleProductImageUpload}
                        uploading={uploading}
                      />
                    </div>

                    <ProductVideoEditor
                      videoUrl={currentProduct.videoUrl}
                      videoId={currentProduct.videoId}
                      languageCode={activeProductLanguage}
                      uploading={productVideoUploading}
                      onUpload={handleProductVideoUpload}
                    />
                  </div>

                  <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-cyan-950">Saat · Tüm Dillerde Ortak</h3>
                        <p className="mt-1 text-xs text-cyan-800">Bu seçim ürünün bütün dillerinde aynı kalır.</p>
                      </div>
                      <select
                        value={currentBaseProduct.sharedTimeValue || ""}
                        onChange={(e) => setCurrentProductFieldDraft("sharedTimeValue", e.target.value)}
                        className="w-full rounded-lg border border-cyan-300 bg-white px-3 py-2 text-sm font-semibold text-cyan-950 sm:w-48"
                      >
                        {PRODUCT_STANDARD_DURATION_OPTIONS.map((option) => (
                          <option key={option.value || "none"} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-4">
                    <div>
                      <h3 className="text-sm font-bold text-fuchsia-950">Menü ve Opsiyon Davranışı · Tüm Dillerde Ortak</h3>
                      <p className="mt-1 text-xs text-fuchsia-800">
                        Fiyat etiketi ve POS/fiş hizmet yapısı bütün dillerde aynı ayarı kullanır.
                      </p>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                      <button
                        type="button"
                        onClick={() =>
                          setCurrentProductFieldDraft(
                            "showOffersInsteadOfPrice",
                            !currentBaseProduct.showOffersInsteadOfPrice
                          )
                        }
                        className={`rounded-xl border p-4 text-left transition ${
                          currentBaseProduct.showOffersInsteadOfPrice
                            ? "border-amber-500 bg-amber-100 text-amber-950"
                            : "border-slate-300 bg-white text-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-bold">Teklifleri Gör Ekle</span>
                          <span className="text-sm font-extrabold">
                            {currentBaseProduct.showOffersInsteadOfPrice ? "Açık" : "Kapalı"}
                          </span>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed">
                          Açıkken kiosk menüsünde fiyat yerine seçilen dile göre “Teklifleri Gör” yazılır.
                          Kapalıyken 0 TL dahil gerçek fiyat gösterilir.
                        </p>
                      </button>

                      <button
                        type="button"
                        disabled={!!currentBaseProduct.addMainProductToReceipt}
                        onClick={() =>
                          setCurrentProductFieldDraft(
                            "menuProductIsMain",
                            !currentBaseProduct.menuProductIsMain
                          )
                        }
                        className={`rounded-xl border p-4 text-left transition ${
                          currentBaseProduct.addMainProductToReceipt
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-70"
                            : currentBaseProduct.menuProductIsMain
                              ? "border-emerald-500 bg-emerald-100 text-emerald-950"
                              : "border-slate-300 bg-white text-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-bold">Menü Ürünü Ana Ürün Olsun</span>
                          <span className="text-sm font-extrabold">
                            {currentBaseProduct.menuProductIsMain ? "Açık" : "Kapalı"}
                          </span>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed">
                          Kapalıyken seçilen her opsiyon POS ve fişte ayrı hizmet olur. Açıkken opsiyonlar
                          menü ürününün altında eski düzende gösterilir.
                        </p>
                      </button>

                      <button
                        type="button"
                        disabled={!!currentBaseProduct.menuProductIsMain}
                        onClick={() =>
                          setCurrentProductFieldDraft(
                            "addMainProductToReceipt",
                            !currentBaseProduct.addMainProductToReceipt
                          )
                        }
                        className={`rounded-xl border p-4 text-left transition ${
                          currentBaseProduct.menuProductIsMain
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-70"
                            : currentBaseProduct.addMainProductToReceipt
                              ? "border-sky-500 bg-sky-100 text-sky-950"
                              : "border-slate-300 bg-white text-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-bold">Ana Ürünü Fişe Ekle</span>
                          <span className="text-sm font-extrabold">
                            {currentBaseProduct.addMainProductToReceipt ? "Açık" : "Kapalı"}
                          </span>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed">
                          Varsayılan kapalıdır. Kapalıyken müşteri fişinde ana ürün satırı gösterilmez,
                          yalnızca seçilen opsiyonlar gösterilir. “Menü Ürünü Ana Ürün Olsun” açıkken
                          bu seçenek kullanılamaz.
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setCurrentProductFieldDraft(
                            "showRecommendedProducts",
                            !currentBaseProduct.showRecommendedProducts
                          )
                        }
                        className={`rounded-xl border p-4 text-left transition ${
                          currentBaseProduct.showRecommendedProducts
                            ? "border-violet-500 bg-violet-100 text-violet-950"
                            : "border-slate-300 bg-white text-slate-700"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-bold">Önerilen Ürünler Gözüksün mü?</span>
                          <span className="text-sm font-extrabold">
                            {currentBaseProduct.showRecommendedProducts ? "Evet" : "Hayır"}
                          </span>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed">
                          Evet olduğunda bu ana ürün sepete eklendikten sonra Tercihler sayfasında tanımlanan ortak önerilen ürünler gösterilir.
                        </p>
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        {editingField === "name" ? (
                          <div className="w-full">
                            <input
                              type="text"
                              value={tempValue}
                              onChange={(e) => handleTempValueChange(e.target.value)}
                              onBlur={() => updateProductField("name", tempValue)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") updateProductField("name", tempValue);
                              }}
                              className="text-xl md:text-2xl font-bold border-b-2 border-blue-500 outline-none w-full"
                              placeholder={`${getProductLanguageLabel(activeProductLanguage)} ürün ismi`}
                              autoFocus
                            />
                            <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                              {textLimitWarnings.name ? (
                                <span className="text-red-500 font-medium">{textLimitWarnings.name}</span>
                              ) : (
                                <span className="text-gray-400">Maksimum {PRODUCT_NAME_MAX_LENGTH} karakter</span>
                              )}
                              <span className="text-gray-400 whitespace-nowrap">{String(tempValue || "").length}/{PRODUCT_NAME_MAX_LENGTH}</span>
                            </div>
                          </div>
                        ) : (
                          <h1
                            className="text-xl md:text-2xl font-bold text-gray-800 cursor-pointer hover:bg-gray-100 p-1 rounded break-words min-h-10"
                            onClick={() => startEditing("name", currentProduct.name)}
                          >
                            {currentProduct.name || `${getProductLanguageLabel(activeProductLanguage)} ürün ismini eklemek için tıklayın`}
                          </h1>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 p-2">
                        <select
                          value={activeProductLanguage}
                          onChange={(e) => handleProductLanguageChange(e.target.value)}
                          className="rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-bold text-blue-900"
                        >
                          {(currentBaseProduct.localizedData || []).map((locale) => {
                            const code = normalizeProductLanguageCode(locale.languageCode);
                            return <option key={code} value={code}>{getProductLanguageLabel(code)}</option>;
                          })}
                        </select>
                        <select
                          value=""
                          onChange={(e) => e.target.value && handleProductLanguageChange(e.target.value)}
                          className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-bold text-emerald-800"
                        >
                          <option value="">+ Dil Ekle</option>
                          {PRODUCT_LANGUAGE_OPTIONS.filter(
                            (option) => !(currentBaseProduct.languages || []).includes(option.code)
                          ).map((option) => (
                            <option key={option.code} value={option.code}>{option.label}</option>
                          ))}
                        </select>
                        {activeProductLanguage !== "tr" && (
                          <button
                            type="button"
                            onClick={removeActiveProductLanguage}
                            className="rounded-lg bg-red-100 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-200"
                          >
                            Dili Sil
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs font-semibold text-amber-900">
                          {getProductLanguageLabel(activeProductLanguage)} içeriği bağımsızdır. Yeni dil eklediğinizde isim, açıklama, fiyat, KDV, departman, video, video açıklaması, ürün süresi, sorular ve seçimler boş/varsayılan başlar.
                        </p>
                      </div>

                      <div>
                        <VatPriceInput
                          grossValue={currentProduct.basePrice}
                          vatRate={currentProduct.kdv ?? 10}
                          onChangeGross={(g) => setCurrentProductGross(g)}
                          onChangeVat={(r) => setCurrentProductVat(r)}
                          errorKey={"basePrice"}
                          setError={setPriceErrors}
                          size="md"
                          showEntryNote={true}
                        />
                        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setCurrentProductFieldDraft("discountEnabled", !currentProduct.discountEnabled)}
                              className={`rounded-lg px-3 py-2 text-xs font-bold ${currentProduct.discountEnabled ? "bg-rose-600 text-white" : "bg-white text-rose-700 border border-rose-300"}`}
                            >
                              {currentProduct.discountEnabled ? "İndirim Açık" : "İndirim Kapalı"}
                            </button>
                            {currentProduct.discountEnabled && (
                              <label className="flex items-center gap-2 text-xs font-bold text-rose-900">
                                İndirim Oranı
                                <div className="flex items-center rounded-lg border border-rose-300 bg-white px-2 py-1.5">
                                  <span className="mr-1">%</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={currentProduct.discountRate ?? 0}
                                    onChange={(e) => setCurrentProductFieldDraft("discountRate", normalizeDiscountRate(e.target.value))}
                                    className="w-20 bg-transparent outline-none"
                                  />
                                </div>
                              </label>
                            )}
                          </div>
                          {currentProduct.discountEnabled && (() => {
                            const discount = getDiscountBreakdown(currentProduct.basePrice, true, currentProduct.discountRate);
                            return (
                              <div className="mt-2 grid grid-cols-1 gap-1 text-xs sm:grid-cols-3">
                                <span>Normal KDV Dahil: <b>{formatTRY(discount.originalGross)}</b></span>
                                <span>İndirim: <b>%{discount.discountRate} · -{formatTRY(discount.discountAmount)}</b></span>
                                <span className="text-emerald-700">İndirimli KDV Dahil: <b>{formatTRY(discount.discountedGross)}</b></span>
                              </div>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Fiyat, KDV, indirim ve departman seçili dile özeldir. İndirim KDV dahil fiyat üzerinden uygulanır.</p>
                        {priceErrors.basePrice && <p className="text-red-500 text-xs mt-1">{priceErrors.basePrice}</p>}
                      </div>

                      <DepartmentSelect
                        label="Ürün Departmanı"
                        value={currentProduct.departmanno || ""}
                        departments={departments}
                        onChange={setCurrentProductDepartmentNo}
                        helperText="Departman seçimi de seçili dilin ürün içeriğine kaydedilir."
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ürün Açıklaması · {getProductLanguageLabel(activeProductLanguage)}</label>
                        {editingField === "description" ? (
                          <div>
                            <textarea
                              value={tempValue}
                              onChange={(e) => handleTempValueChange(e.target.value)}
                              onBlur={() => updateProductField("description", tempValue)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-16"
                              placeholder={`${getProductLanguageLabel(activeProductLanguage)} ürün açıklaması`}
                              autoFocus
                            />
                            <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                              {textLimitWarnings.description ? (
                                <span className="text-red-500 font-medium">{textLimitWarnings.description}</span>
                              ) : (
                                <span className="text-gray-400">Maksimum {PRODUCT_DESCRIPTION_MAX_LENGTH} karakter</span>
                              )}
                              <span className="text-gray-400 whitespace-nowrap">{String(tempValue || "").length}/{PRODUCT_DESCRIPTION_MAX_LENGTH}</span>
                            </div>
                          </div>
                        ) : (
                          <p
                            className="text-gray-600 cursor-pointer hover:bg-gray-100 p-2 rounded border border-gray-200 text-sm min-h-16 flex items-start"
                            onClick={() => startEditing("description", currentProduct.description || "")}
                          >
                            {currentProduct.description || `${getProductLanguageLabel(activeProductLanguage)} açıklamasını eklemek için tıklayın...`}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video Açıklaması · {getProductLanguageLabel(activeProductLanguage)}</label>
                        <textarea
                          value={currentProduct.videoDescription || ""}
                          onChange={(e) => setCurrentProductFieldDraft("videoDescription", getLimitedText(e.target.value, PRODUCT_VIDEO_DESCRIPTION_MAX_LENGTH))}
                          className="w-full min-h-24 rounded-lg border border-purple-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Bu dilde videonun altında veya yanında gösterilecek açıklamayı yazın..."
                        />
                        <div className="mt-1 text-right text-xs text-gray-400">{String(currentProduct.videoDescription || "").length}/{PRODUCT_VIDEO_DESCRIPTION_MAX_LENGTH}</div>
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <h3 className="text-sm font-bold text-slate-900">Bu Dile Özel Ürün Seçimleri</h3>
                        <p className="mt-1 text-xs text-slate-500">Türkçede açık olan bir seçim İngilizcede kapalı olabilir.</p>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <button
                            type="button"
                            onClick={() => setProductModeFlag("personnelEnabled")}
                            className={`rounded-xl border p-4 text-left transition ${currentProduct.personnelEnabled ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-white text-slate-600"}`}
                          >
                            <div className="flex items-center justify-between"><span className="font-bold">Personel</span><span>{currentProduct.personnelEnabled ? "Açık" : "Kapalı"}</span></div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setProductModeFlag("timeEnabled")}
                            className={`rounded-xl border p-4 text-left transition ${currentProduct.timeEnabled ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-300 bg-white text-slate-600"}`}
                          >
                            <div className="flex items-center justify-between"><span className="font-bold">Saat</span><span>{currentProduct.timeEnabled ? "Açık" : "Kapalı"}</span></div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setProductModeFlag("dayEnabled")}
                            className={`rounded-xl border p-4 text-left transition ${currentProduct.dayEnabled ? "border-violet-500 bg-violet-50 text-violet-900" : "border-slate-300 bg-white text-slate-600"}`}
                          >
                            <div className="flex items-center justify-between"><span className="font-bold">Gün</span><span>{currentProduct.dayEnabled ? "Açık" : "Kapalı"}</span></div>
                          </button>
                        </div>
                        {currentProduct.dayEnabled && (
                          <p className="mt-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-medium text-violet-900">
                            Gün açık olduğu için Personel ve Saat kapatıldı. Opsiyon süresinde yalnızca +0, gün ve ay seçenekleri kullanılabilir.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-80 lg:ml-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3 text-center">Ürün Önizlemesi</h3>
                    <ProductPreviewCard product={currentProduct} />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between space-y-3 md:space-y-0 pt-4 border-t border-gray-200">
                <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-3">
                  <button
                    onClick={handleSaveChanges}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-300 ${
                      hasUnsavedChanges
                        ? "bg-green-500 hover:bg-green-600 text-white transform hover:scale-105 shadow-lg"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                    }`}
                    disabled={!hasUnsavedChanges || saving}
                  >
                    <span className="text-lg">💾</span>
                    <span className="font-semibold">{saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}</span>
                    {hasUnsavedChanges && !saving && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
                  </button>

                  {hasUnsavedChanges && (
                    <button
                      onClick={handleDiscardChanges}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-all duration-300"
                      disabled={saving}
                    >
                      <span className="text-lg">↶</span>
                      <span className="font-semibold">Değişiklikleri İptal Et</span>
                    </button>
                  )}

                  <button
                    onClick={removeProduct}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-all duration-300"
                    disabled={saving}
                  >
                    <span className="text-lg">🗑️</span>
                    <span className="font-semibold">Bu Ürünü Sil</span>
                  </button>
                </div>

                {showSaveSuccess && (
                  <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg animate-fade-in">
                    <div className="flex items-center justify-center space-x-2">
                      <span>✓</span>
                      <span className="text-sm">Değişiklikler başarıyla kaydedildi!</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {currentProduct.questions && currentProduct.questions.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Soru Navigasyonu · {getProductLanguageLabel(activeProductLanguage)}</h3>
                <div className="flex flex-wrap gap-3 max-h-40 overflow-y-auto">
                  {currentProduct.questions.map((question, index) => (
                    <button
                      key={question.id}
                      onClick={() => handleNavigationClick(index)}
                      title={question.text}
                      className={`${QUESTION_NAV_BTN} ${
                        activeQuestion === index
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {question.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={questionsSectionRef} className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between space-y-2 md:space-y-0">
                <h2 className="text-xl font-bold text-gray-800">Özelleştirme Soruları · {getProductLanguageLabel(activeProductLanguage)}</h2>
                <button
                  onClick={handleAddQuestionClick}
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors text-sm md:text-base w-full md:w-auto"
                >
                  + Soru Ekle
                </button>
              </div>

              {currentProduct.questions && currentProduct.questions.length > 0 ? (
                currentProduct.questions.map((question, questionIndex) => (
                  <div
                    key={question.id}
                    ref={(el) => (questionRefs.current[questionIndex] = el)}
                    className={`bg-white rounded-lg shadow-md p-4 md:p-6 transition-all duration-300 ${
                      activeQuestion === questionIndex ? "border-2 border-blue-500" : "border border-gray-200"
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 space-y-2 md:space-y-0">
                      <div className="flex-1">
                        {editingField === `question-${questionIndex}-text` ? (
                          <input
                            type="text"
                            value={tempValue}
                            onChange={(e) => handleTempValueChange(e.target.value)}
                            onBlur={() => updateQuestionField(questionIndex, "text", tempValue)}
                            onKeyDown={(e) => e.key === "Enter" && updateQuestionField(questionIndex, "text", tempValue)}
                            className="text-lg font-semibold border-b-2 border-blue-500 outline-none w-full"
                            autoFocus
                          />
                        ) : (
                          <h3
                            className="text-lg font-semibold text-gray-800 cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => startEditing(`question-${questionIndex}-text`, question.text)}
                          >
                            {question.text}
                          </h3>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 md:flex-nowrap md:items-center md:space-x-2 md:ml-4">
                        <select
                          value={question.isRequired ? "required" : "optional"}
                          onChange={(e) => updateQuestionField(questionIndex, "isRequired", e.target.value === "required")}
                          className="border rounded px-2 py-1 text-sm w-full md:w-auto"
                        >
                          <option value="optional">İsteğe Bağlı</option>
                          <option value="required">Zorunlu</option>
                        </select>

                        <select
                          value={question.type}
                          onChange={(e) => updateQuestionField(questionIndex, "type", e.target.value)}
                          className="border rounded px-2 py-1 text-sm w-full md:w-auto"
                        >
                          <option value="multiple">Çoktan Seçmeli</option>
                          <option value="single">Tek Seçimli</option>
                        </select>

                        <button
                          onClick={() => deleteQuestion(questionIndex)}
                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded text-sm w-full md:w-auto"
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between space-y-2 md:space-y-0">
                        <div>
                          <h4 className="font-medium text-gray-700">Seçenekler</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Aynı şey soruların fiyatı için de geçerlidir: kuruş eklemek istiyorsanız "." eklemeniz gereklidir. (Maksimum 2 hane)
                          </p>
                        </div>

                        <button
                          onClick={() => addNewOption(questionIndex)}
                          className="bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded text-sm w-full md:w-auto"
                        >
                          + Seçenek Ekle
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {question.options &&
                          question.options.map((option, optionIndex) => {
                            const errKey = `option-${questionIndex}-${optionIndex}`;

                            return (
                              <div key={option.id} className="border rounded-lg p-4">
                                <div className="flex space-x-4">
                                  <div className="relative">
                                    <OptionImage
                                      src={option.image}
                                      alt={option.name}
                                      onImageChange={(e) => handleOptionImageUpload(e, questionIndex, optionIndex)}
                                      uploading={uploading}
                                    />
                                  </div>

                                  <div className="flex-1">
                                    <div className="mb-2">
                                      {editingField === `option-${questionIndex}-${optionIndex}-name` ? (
                                        <input
                                          type="text"
                                          value={tempValue}
                                          onChange={(e) => handleTempValueChange(e.target.value)}
                                          onBlur={() => updateOptionField(questionIndex, optionIndex, "name", tempValue)}
                                          onKeyDown={(e) =>
                                            e.key === "Enter" &&
                                            updateOptionField(questionIndex, optionIndex, "name", tempValue)
                                          }
                                          className="font-medium border-b-2 border-blue-500 outline-none w-full"
                                          autoFocus
                                        />
                                      ) : (
                                        <p
                                          className="font-medium text-gray-800 cursor-pointer hover:bg-gray-100 p-1 rounded"
                                          onClick={() =>
                                            startEditing(`option-${questionIndex}-${optionIndex}-name`, option.name)
                                          }
                                        >
                                          {option.name}
                                        </p>
                                      )}
                                    </div>

                                    <div className="space-y-2">
                                      <div>
                                        <VatPriceInput
                                          grossValue={option.price}
                                          vatRate={option.kdvoption ?? 10}
                                          onChangeGross={(g) => setOptionGross(questionIndex, optionIndex, g)}
                                          onChangeVat={(r) => setOptionVat(questionIndex, optionIndex, r)}
                                          errorKey={errKey}
                                          setError={setPriceErrors}
                                          size="sm"
                                          showEntryNote={false}
                                        />
                                        {priceErrors[errKey] && (
                                          <p className="text-red-500 text-xs mt-1">{priceErrors[errKey]}</p>
                                        )}
                                        <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => toggleFlag("discountEnabled", questionIndex, optionIndex)}
                                              className={`px-2 py-1 rounded text-xs font-bold ${option.discountEnabled ? "bg-rose-600 text-white" : "bg-white text-rose-700 border border-rose-300"}`}
                                            >
                                              {option.discountEnabled ? "İndirim Açık" : "İndirim Kapalı"}
                                            </button>
                                            {option.discountEnabled && (
                                              <label className="flex items-center gap-1 text-xs font-bold text-rose-900">
                                                %
                                                <input
                                                  type="number"
                                                  min="0"
                                                  max="100"
                                                  step="0.01"
                                                  value={option.discountRate ?? 0}
                                                  onChange={(e) => setOptionFieldDraft(questionIndex, optionIndex, "discountRate", normalizeDiscountRate(e.target.value))}
                                                  className="w-20 rounded border border-rose-300 bg-white px-2 py-1 outline-none"
                                                />
                                              </label>
                                            )}
                                          </div>
                                          {option.discountEnabled && (() => {
                                            const discount = getDiscountBreakdown(option.price, true, option.discountRate);
                                            return (
                                              <div className="mt-1 text-[11px] leading-5">
                                                <div>Normal: <b>{formatTRY(discount.originalGross)}</b></div>
                                                <div>İndirim: <b>%{discount.discountRate} · -{formatTRY(discount.discountAmount)}</b></div>
                                                <div className="text-emerald-700">İndirimli KDV Dahil: <b>{formatTRY(discount.discountedGross)}</b></div>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>

                                      <DepartmentSelect
                                        label="Ekstra Departmanı"
                                        value={option.departmanno || ""}
                                        departments={departments}
                                        onChange={(departmentNo) =>
                                          setOptionDepartmentNo(questionIndex, optionIndex, departmentNo)
                                        }
                                        size="sm"
                                      />

                                      <div className="flex flex-wrap items-center gap-2">
                                        <button
                                          onClick={() => toggleFlag("hasQuantity", questionIndex, optionIndex)}
                                          className={`px-2 py-1 rounded text-xs ${
                                            option.hasQuantity ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
                                          }`}
                                        >
                                          {option.hasQuantity ? "Adet Var" : "Adet Yok"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => toggleFlag("durationEnabled", questionIndex, optionIndex)}
                                          className={`px-2 py-1 rounded text-xs ${
                                            option.durationEnabled ? "bg-orange-100 text-orange-800" : "bg-gray-100 text-gray-800"
                                          }`}
                                        >
                                          {option.durationEnabled ? "Süre Açık" : "Süre Kapalı"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => toggleFlag("durationVisible", questionIndex, optionIndex)}
                                          className={`px-2 py-1 rounded text-xs ${
                                            option.durationVisible !== false
                                              ? "bg-emerald-100 text-emerald-800"
                                              : "bg-gray-100 text-gray-800"
                                          }`}
                                          title="Bu ayar yalnızca kiosk ekranındaki süre etiketini kontrol eder. Süre hesabı değişmez."
                                        >
                                          {option.durationVisible !== false ? "Süreyi Göster" : "Süreyi Gösterme"}
                                        </button>
                                      </div>

                                      {option.durationEnabled && (
                                        <div>
                                          <label className="mb-1 block text-xs font-medium text-gray-600">
                                            Opsiyon Süresi {currentProduct.dayEnabled ? "· +0 / Gün / Ay" : "· +0 / Dakika / Saat"}
                                          </label>
                                          <select
                                            value={option.durationValue || ""}
                                            onChange={(e) => setOptionFieldDraft(questionIndex, optionIndex, "durationValue", e.target.value)}
                                            className="w-full rounded border border-orange-300 bg-orange-50 px-2 py-1.5 text-xs text-orange-950"
                                          >
                                            {getProductDurationOptions(!!currentProduct.dayEnabled).map((durationOption) => (
                                              <option key={durationOption.value || "none"} value={durationOption.value}>{durationOption.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                      )}

                                      <button
                                        onClick={() => deleteOption(questionIndex, optionIndex)}
                                        className="bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded text-xs"
                                      >
                                        Sil
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <p className="text-gray-500 text-lg">Henüz soru yok. Başlamak için ilk sorunuzu ekleyin!</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500 text-lg">
                {filteredProducts.length === 0 ? `${currentCategory?.label} kategorisinde ürün bulunmuyor` : "Ürün seçilmedi"}
              </p>
              <button
                onClick={addNewProduct}
                className="mt-4 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                + Yeni Ürün Ekle
              </button>
            </div>
          </div>
        )}

        {showAddQuestionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Yeni Soru Ekle</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Soru Metni</label>
                  <input
                    type="text"
                    value={newQuestion.text}
                    onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                    placeholder="Soru metnini girin..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Soru Tipi</label>
                    <select
                      value={newQuestion.type}
                      onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="multiple">Çoktan Seçmeli</option>
                      <option value="single">Tek Seçimli</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zorunlu mu?</label>
                    <select
                      value={newQuestion.isRequired ? "required" : "optional"}
                      onChange={(e) => setNewQuestion({ ...newQuestion, isRequired: e.target.value === "required" })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="optional">İsteğe Bağlı</option>
                      <option value="required">Zorunlu</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowAddQuestionModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleAddQuestion}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Soru Ekle
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showAddCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Yeni Kategori Ekle</h3>

              {error && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
              )}

              <div className="space-y-4">
                <div>
                  <CategoryLanguageEditor
                    localizedData={newCategory.localizedData}
                    activeLanguage={activeNewCategoryLanguage}
                    onActiveLanguageChange={setActiveNewCategoryLanguage}
                    onLabelChange={(languageCode, label) => updateCategoryLocaleState(setNewCategory, languageCode, label)}
                    onAddLanguage={(languageCode) => addCategoryLocaleState(setNewCategory, languageCode)}
                    onDeleteLanguage={(languageCode) => deleteCategoryLocaleState(setNewCategory, languageCode, setActiveNewCategoryLanguage)}
                    disabled={uploading}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Yeni kategori sıraya otomatik olarak en alta eklenecek: <strong>{getNextCategorySira(categories)}. sıra</strong>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kategori İkonu *</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                        {newCategory.iconFile ? (
                          <img
                            src={URL.createObjectURL(newCategory.iconFile)}
                            alt="Kategori ikonu önizlemesi"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center">
                            <span>İkon Yok</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex-1">
                      <label className="block bg-blue-500 hover:bg-blue-600 text-white text-center py-2 px-3 rounded-lg transition-colors cursor-pointer">
                        {newCategory.iconFile ? "İkonu Değiştir" : "İkon Yükle"}
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setNewCategory((previous) => ({ ...previous, iconFile: file, icon: URL.createObjectURL(file) }));
                              setError(null);
                            }
                          }}
                        />
                      </label>

                      <p className="text-xs text-gray-500 mt-1">
                        {newCategory.iconFile ? `Seçilen: ${newCategory.iconFile.name}` : "Dosya seçilmedi"}
                      </p>

                      <p className="text-xs text-gray-400 mt-1">
                        Desteklenen formatlar: JPG, JPEG, PNG, WebP, GIF, SVG, BMP, TIFF (Maks. 8MB)
                      </p>
                    </div>
                  </div>
                </div>

                {uploading && (
                  <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                      <span>Fotoğraf yükleniyor...</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowAddCategoryModal(false);
                      setError(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={uploading}
                  >
                    İptal
                  </button>

                  <button
                    onClick={handleAddCategory}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      uploading || normalizeCategoryLocalizedData(newCategory).some((item) => !item.label.trim()) || !newCategory.iconFile
                        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                    disabled={uploading || normalizeCategoryLocalizedData(newCategory).some((item) => !item.label.trim()) || !newCategory.iconFile}
                  >
                    {uploading ? "Yükleniyor..." : "Kategori Ekle"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEditCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Kategoriyi Düzenle</h3>

              {error && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
              )}

              <div className="space-y-4">
                <div>
                  <CategoryLanguageEditor
                    localizedData={editingCategoryData.localizedData}
                    activeLanguage={activeEditCategoryLanguage}
                    onActiveLanguageChange={setActiveEditCategoryLanguage}
                    onLabelChange={(languageCode, label) => updateCategoryLocaleState(setEditingCategoryData, languageCode, label)}
                    onAddLanguage={(languageCode) => addCategoryLocaleState(setEditingCategoryData, languageCode)}
                    onDeleteLanguage={(languageCode) => deleteCategoryLocaleState(setEditingCategoryData, languageCode, setActiveEditCategoryLanguage)}
                    disabled={saving}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Sabit kategori ID: <strong>{editingCategoryData.id || editingCategory?.id}</strong>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Eski Türkçe kategori adı: <strong>{editingCategoryData.oldLabel}</strong>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Mevcut sıra: <strong>{normalizeCategoryOrderValue(editingCategoryData.sira, 1)}</strong>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kategori İkonu</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                        {editingCategoryData.icon ? (
                          <img src={editingCategoryData.icon} alt="Kategori ikonu" className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center">
                            <span>İkon Yok</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex-1">
                      <label
                        className={`block text-center py-2 px-3 rounded-lg transition-colors cursor-pointer ${
                          saving ? "bg-gray-400 text-gray-200 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600 text-white"
                        }`}
                      >
                        İkon Yükle
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (evt) => {
                                setEditingCategoryData((previous) => ({ ...previous, icon: evt.target.result }));
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          disabled={saving}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {saving && (
                  <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                      <span>Kategori güncelleniyor...</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={handleEditCategoryCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={saving}
                  >
                    İptal
                  </button>

                  <button
                    onClick={handleEditCategorySave}
                    disabled={saving || normalizeCategoryLocalizedData(editingCategoryData).some((item) => !item.label.trim())}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      saving || normalizeCategoryLocalizedData(editingCategoryData).some((item) => !item.label.trim())
                        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSortCategoryModal && categoryToSort && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Kategori Sırasını Değiştir</h3>

              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800 font-medium">{categoryToSort.label}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Mevcut sıra: <strong>{normalizeCategoryOrderValue(categoryToSort.sira, 1)}</strong>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Yeni sıra numarası</label>
                  <input
                    type="number"
                    min="1"
                    max={categories.length}
                    value={sortOrderValue}
                    onChange={(e) => setSortOrderValue(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder={`1 - ${categories.length}`}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Yazdığınız sıradaki kategoriyle bu kategori yer değiştirecek.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Geçerli aralık: <strong>1</strong> ile <strong>{categories.length}</strong> arası
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={handleCloseSortCategoryModal}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={saving}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSortCategorySave}
                    disabled={saving}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      saving ? "bg-gray-400 text-gray-200 cursor-not-allowed" : "bg-amber-500 text-white hover:bg-amber-600"
                    }`}
                  >
                    {saving ? "Sıralanıyor..." : "Sırayı Güncelle"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showUnsavedChangesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {pendingRedirect ? getRedirectModalTitle(pendingRedirect) : "Kaydedilmemiş Değişiklikler"}
              </h3>

              <div className="space-y-4">
                <p className="text-gray-600">
                  {pendingRedirect
                    ? getRedirectModalDesc(pendingRedirect)
                    : "Kaydedilmemiş değişiklikleriniz var. Ne yapmak istiyorsunuz?"}
                </p>

                <div className="flex flex-col space-y-3 pt-4">
                  <button
                    onClick={handleSaveAndContinue}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg transition-colors font-medium"
                    disabled={saving}
                  >
                    {saving
                      ? "Kaydediliyor..."
                      : pendingRedirect
                      ? getRedirectSaveText(pendingRedirect)
                      : "Değişiklikleri Kaydet ve Devam Et"}
                  </button>

                  <button
                    onClick={handleContinueWithoutSaving}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg transition-colors font-medium"
                    disabled={saving}
                  >
                    {pendingRedirect ? getRedirectNoSaveText(pendingRedirect) : "Kaydetmeden Devam Et"}
                  </button>

                  <button
                    onClick={handleCancelSwitch}
                    className="w-full bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg transition-colors font-medium"
                    disabled={saving}
                  >
                    İptal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Kategoriyi Sil</h3>

              <div className="space-y-4">
                <p className="text-gray-600">
                  Bu kategoriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve bu kategoriye ait tüm ürünler de
                  silinecektir.
                </p>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={cancelDeleteCategory}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={saving}
                  >
                    İptal
                  </button>
                  <button
                    onClick={confirmDeleteCategory}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    disabled={saving}
                  >
                    {saving ? "Siliniyor..." : "Evet, Sil"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeletingProductModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500 mx-auto mb-3"></div>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Lütfen bekleyiniz</h3>
              <p className="text-gray-600">Ürün siliniyor...</p>
            </div>
          </div>
        )}

        {showProductDeletedModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4 text-center">
              <div className="text-green-500 text-4xl mb-2">✅</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Başarılı</h3>
              <p className="text-gray-600">Ürün başarılı bir şekilde silindi.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantProductManagement;
